import React from "react";
import {
 Card,
 Elevation,
 Button,
 Icon,
 Spinner,
 NonIdealState,
 Tabs,
 Tab,
 AnchorButton,
 Tag,
 Dialog,
 Classes,
 FormGroup,
 HTMLSelect,
 MenuItem,
 TextArea,
 InputGroup,
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Suggest } from "@blueprintjs/select";
import { DateInput } from "@blueprintjs/datetime";
import { inject, observer } from "mobx-react";
import {
 apiCallPost,
 apiCallGet,
 apiCallPostMultipartProgress,
 apiCallPatch,
 printApiUrl,
} from "../../../micro/fns/util";
import {
 getStatusMeta as getStatusMetaGlobal,
 getDisplayStatusMeta as getDisplayStatusMetaGlobal,
} from "../../constants/statusColors";
import cogoToast from "cogo-toast";
import moment from "moment";
import { withRouter } from "react-router-dom";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";
import { buildProcedureDischargeSummaryName } from "../../utils/procedureSummary";
import { isSxBillingSuperuser } from "../../utils/sxBillingSuperuser";

// Import tab components
import {
 SxBookingDetailsTab,
 SystemicParamsTab,
 KeyEventsTab,
 SurgeryVideoTab,
 DischargeTab,
 ConsumptionTab,
 BillingTab,
 AuditTab,
} from "./surgery-booking-details";

// Cross-page cache for master data in details too
if (typeof window !== "undefined") {
 window.__neoCache = window.__neoCache || {};
 window.__neoInflight = window.__neoInflight || {};
 // Ensure booking-level caches exist
 window.__neoCache.bookingById = window.__neoCache.bookingById || {};
 window.__neoInflight.bookingById = window.__neoInflight.bookingById || {};
}

class SurgeryBookingDetails extends React.Component {
 constructor(props) {
  super(props);
  this.auditTabRef = React.createRef();
  this.billingTabRef = React.createRef();
  // Prevent accidental double init
  this._initDone = false;
  // Measure a representative header row height for button alignment
  this.headerRowHeightRef = React.createRef();
 }

 state = {
  // Basic state
  surgery: null,
  isLoading: true,
  error: null,
  activeTab: "sx-booking",
  // Signal to force Audit tab reloads on selection
  auditRefreshTick: 0,

  // Systemic parameters (V3 copy)
  systemicInvestigations: [],

  // Key People & Events
  keyPeople: [],
  keyEvents: [],

  // Tab 1: SX Booking Details
  sxBooking: {
   surgeon: "",
   secondarySurgeon: "",
   anaesthetist: "",
   chosenBy: "",
   date: new Date(),
   time: new Date(),
   otTheatre: "",
   room: "",
   laterality: "RE",
   diet: "",
   diabetic: false,
   surgeries: [],
   notes: "",
   postponementNotes: "",
  },

  // UI State
  isSaving: false,
  isSavingMedicalTeam: false,
  isSavingSchedule: false,
  isSavingClinicalDetails: false,
  isSavingPatientDetails: false,
  isPostponingSurgery: false,
  isCancelling: false,
  isCheckingCancelEligibility: false,
  cancelEligibilityError: null,
  isDischarging: false,
  isRevertingDischarge: false,
  isSavingPayorPanelOverride: false,
   // Cancel dialog state
  showCancelDialog: false,
  cancelReason: "",
  isSubmittingCancel: false,
  // Mark discharged confirmation dialog state (separate from cancel)
  showMarkDischargedDialog: false,
  isSubmittingMarkDischarged: false,
  isCheckingDischarge: false,
  dischargeBlockers: [],
  dischargeWarnings: [],

  // Header alignment state
  headerRowHeight: 0,

  // Inline edit: patient details
  isEditingDetails: false,
  editPatientName: "",
  editDob: null,
  editSex: "",
  editPatientPhone: "",
  editAddress: "",

  // Original data for comparison
  originalSxBooking: null,

  // Dirty state tracking
  isMedicalTeamDirty: false,
  isScheduleDirty: false,
  isClinicalDetailsDirty: false,
  isNotesDirty: false,
  // Files (Systemic Params attachments)
  files: [],
  isLoadingFiles: false,
  isUploadingFile: false,
  uploadProgressDone: 0,
  uploadProgressTotal: 1,
  newFileBlob: null,
  previewFile: null,
  isPreviewOpen: false,

  // Master Data
  consultants: [],
  isLoadingConsultants: false,
  // Anesthesia Types Master
  anesthesiaTypes: [],
  anesthesiaOptions: [], // Full options with value, label, rate
  isLoadingAnesthesiaTypes: false,

  // Consumption Tab State
  itemConsumptions: [],
  consumptionSearchTerm: "",
  consumptionSearchResults: [],
  isSearchingConsumption: false,
  showConsumptionDialog: false,
  selectedConsumptionItems: [],
  consumptionForm: {
   consumptionDate: new Date(),
   consumingDoctorId: "",
   consumingDoctorName: "",
   consumingPatientId: "",
   consumingPatientName: "",
   consumptionHead: "",
   consumptionOT: "",
   notes: "",
   requestedConsumptions: 1,
  },
  isCreatingConsumption: false,
  // Consumption Head options
  consumptionHeadOptions: [
   "Cornea",
   "Cataract",
   "Glaucoma",
   "Occuloplasty",
   "Retina",
   "Lasik",
   "Other",
   "Anaesthesia",
   "OPD",
   "Ward",
  ],

  // Location selection for consumption
  selectedLocation: null,
  availableLocations: [],
  isLoadingLocations: false,

  // Add Surgery dialog state
  showAddSurgeryDialog: false,
  printedNames: [], // {_id, printedName, payor}
  isLoadingPrintedNames: false,
  selectedPrintedNameId: "",
  surgerySearchQuery: "", // Track search query for better UX
 };

 autoUnbillInFlight = false;

 // Accurate age (years) from DOB, accounting for month/day
 calculateAgeFromDob = (dob) => {
  if (!dob) return null;
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
   age -= 1;
  }
  return age;
 };

 componentDidMount() {
  if (this._initDone) return;
  this._initDone = true;
  this.loadSurgeryDetails();
  this.loadConsultants();
  this.loadAnesthesiaTypes();
  // Initial header height measure (async after first paint)
  setTimeout(this.updateHeaderRowHeight, 0);
  try {
   window.addEventListener("resize", this.updateHeaderRowHeight);
  } catch (e) {}
 }

 componentDidUpdate(prevProps, prevState) {
  // Recalculate header row height on changes affecting layout
  if (
   prevState.isEditingDetails !== this.state.isEditingDetails ||
   prevState.surgery !== this.state.surgery ||
   prevState.activeTab !== this.state.activeTab
  ) {
   this.updateHeaderRowHeight();
  }
 }

 componentWillUnmount() {
  try {
   window.removeEventListener("resize", this.updateHeaderRowHeight);
  } catch (e) {}
 }

 updateHeaderRowHeight = () => {
  try {
   const el = this.headerRowHeightRef?.current;
   const measured = el && el.offsetHeight ? el.offsetHeight : 0;
   if (measured && measured !== this.state.headerRowHeight) {
    this.setState({ headerRowHeight: measured });
   }
  } catch (e) {}
 };

 // Clamp a date within [min, max]
 clampDate = (date, min, max) => {
  try {
   if (!date) return null;
   const d = date instanceof Date ? new Date(date.getTime()) : new Date(date);
   if (isNaN(d)) return null;
   const minDate = min instanceof Date ? min : min ? new Date(min) : null;
   const maxDate = max instanceof Date ? max : max ? new Date(max) : null;
   if (minDate && d < minDate) return minDate;
   if (maxDate && d > maxDate) return maxDate;
   return d;
  } catch (e) {
   return date || null;
  }
 };

 // Utils: identify and merge consumptions without losing prior entries
 getConsumptionKey = (c) =>
  c?._id ||
  c?.consumptionId ||
  c?.consumptionNumber ||
  `${c?.createdAt || ""}-${c?.requestedConsumptions || ""}`;

 mergeConsumptions = (existing = [], incoming = []) => {
  try {
   const map = new Map();
   existing.forEach((c) => {
    const key = this.getConsumptionKey(c);
    if (key) map.set(key, c);
   });
   incoming.forEach((c) => {
    const key = this.getConsumptionKey(c);
    if (key) {
     map.set(key, { ...map.get(key), ...c });
    }
   });
   return Array.from(map.values());
  } catch (e) {
   // Fallback to concatenation if anything goes wrong
   return [...existing, ...incoming];
  }
 };

 // Parse time from API response
 parseTimeFromAPI = (timeString) => {
  if (!timeString) return null;

  try {
   // Handle different time formats
   let time = timeString;

   // If it's already in HH:mm format, use it directly
   if (/^\d{1,2}:\d{2}$/.test(timeString)) {
    time = timeString;
   }
   // If it's in HH:mm AM/PM format, convert to 24-hour
   else if (/^\d{1,2}:\d{2}\s*(AM|PM)$/i.test(timeString)) {
    time = moment(timeString, "h:mm A").format("HH:mm");
   }
   // If it's an ISO string, extract time part
   else if (timeString.includes("T")) {
    const parsed = moment(timeString);
    if (parsed.isValid()) {
     time = parsed.format("HH:mm");
    }
   }

   // Validate the time format before creating Date object
   if (/^\d{1,2}:\d{2}$/.test(time)) {
    // Parse hours and minutes to validate range
    const [hours, minutes] = time.split(":").map(Number);
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
     console.warn("Time out of range:", time);
     return null;
    }

    // Create a proper date object for the time picker using today's date
    const today = new Date();
    const parsed = new Date(
     today.getFullYear(),
     today.getMonth(),
     today.getDate(),
     hours,
     minutes,
     0
    );

    // Validate the created date
    if (isNaN(parsed.getTime())) {
     console.warn("Failed to create valid date for time:", time);
     return null;
    }

    return parsed;
   } else {
    console.warn("Invalid time format:", timeString);
    return null;
   }
  } catch (error) {
   console.error("Error parsing time:", timeString, error);
   return null;
  }
 };

 // Normalize schedule time for API (keep AM/PM format for consistency)
 formatScheduleTime = (timeValue) => {
  if (!timeValue) return null;
  if (timeValue instanceof Date) {
   return moment(timeValue).format("hh:mm A");
  }
  if (typeof timeValue === "string") {
   const parsed = moment(
    timeValue,
    [moment.ISO_8601, "HH:mm", "H:mm", "hh:mm A", "h:mm A"],
    true
   );
   if (parsed.isValid()) return parsed.format("hh:mm A");
   const fallback = moment(timeValue);
   return fallback.isValid() ? fallback.format("hh:mm A") : timeValue;
  }
  return null;
 };

 // Build/normalize timeFrame based on scheduled time
 buildScheduleTimeFrame = (timeValue, existingTimeFrame, estimatedDuration) => {
  const normalizedTime = this.formatScheduleTime(timeValue);
  if (!normalizedTime) return existingTimeFrame || "";

  let durationMinutes = null;
  if (existingTimeFrame && existingTimeFrame.includes(" - ")) {
   const [startStr, endStr] = existingTimeFrame.split(" - ");
   const start = moment(startStr, ["hh:mm A", "HH:mm"]);
   const end = moment(endStr, ["hh:mm A", "HH:mm"]);
   if (start.isValid() && end.isValid()) {
    const diff = end.diff(start, "minutes");
    durationMinutes = diff > 0 ? diff : null;
   }
  }

  if (!durationMinutes) {
   const numericDuration = Number(estimatedDuration || 0);
   durationMinutes = numericDuration > 0 ? numericDuration : null;
  }

  if (!durationMinutes) {
   return existingTimeFrame || normalizedTime;
  }

  const startMoment = moment(normalizedTime, "hh:mm A");
  if (!startMoment.isValid()) return existingTimeFrame || normalizedTime;
  const endMoment = startMoment.clone().add(durationMinutes, "minutes");
  return `${startMoment.format("hh:mm A")} - ${endMoment.format("hh:mm A")}`;
 };

 // Helper function to validate MongoDB ObjectID format
 isValidObjectId = (id) => {
  // Check if id is a valid MongoDB ObjectID (24 hex characters)
  return /^[0-9a-fA-F]{24}$/.test(id);
 };

 loadSurgeryDetails = async () => {
  try {
   this.setState({ isLoading: true, error: null });
   const surgeryId = this.props.match.params.id;

   if (!surgeryId) {
    throw new Error("Surgery ID not provided");
   }

   // Validate ObjectID format
   if (!this.isValidObjectId(surgeryId)) {
    throw new Error(
     "Invalid surgery ID format. Please check the URL and try again."
    );
   }

   let response;
   // Check if cached data exists and has implantInvoices field (cache validation)
   const cachedData = window.__neoCache?.bookingById?.[surgeryId];
   const isCacheValid =
    cachedData &&
    "implantInvoices" in cachedData &&
    "payments" in cachedData &&
    "consolidatedBilling" in cachedData;

   if (isCacheValid) {
    console.log(
     "✅ [SurgeryBookingDetails] Using valid cached data with implantInvoices"
    );
    response = {
     success: true,
     data: cachedData,
    };
   } else {
    // Invalid or missing cache - fetch fresh data
    if (cachedData && !("implantInvoices" in cachedData)) {
     console.log(
      "⚠️ [SurgeryBookingDetails] Cache exists but missing implantInvoices - forcing refresh"
     );
     delete window.__neoCache.bookingById[surgeryId];
    }

    if (!window.__neoInflight?.bookingById?.[surgeryId]) {
     window.__neoInflight.bookingById[surgeryId] = apiCallGet(
      `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getById/${surgeryId}`
     ).finally(() => {
      // Clean up inflight slot after resolution
      delete window.__neoInflight.bookingById[surgeryId];
     });
    }
    response = await window.__neoInflight.bookingById[surgeryId];
    if (response?.success && response?.data) {
     window.__neoCache.bookingById[surgeryId] = response.data;
    }
   }

   if (response.success && response.data) {
    // Transform the API response to match our component structure
    const normalizedStatus = (response.data.status || "").toLowerCase();
    const shouldClearScheduleFields = normalizedStatus === "postponed";
    const scheduledDateFromApi = shouldClearScheduleFields
     ? null
     : response.data.scheduledDate;
    const scheduledTimeFromApi = shouldClearScheduleFields
     ? null
     : response.data.scheduledTime;

    // Use _id if id is not available or invalid, fallback to the URL parameter
    const surgeryDataId = response.data._id || response.data.id || surgeryId;

    // Debug logging to help identify the issue
    console.log("Surgery data received:", {
     responseDataId: response.data.id,
     responseData_Id: response.data._id,
     urlSurgeryId: surgeryId,
     finalId: surgeryDataId,
     isValidFinalId: this.isValidObjectId(surgeryDataId),
    });

    // Compute age if not provided by API
    const computedAge = this.calculateAgeFromDob(
     response.data.patientDob || response.data.patientOriginalData?.dob
    );

    const surgery = {
     id: surgeryDataId,
     patientId: response.data.patientId,
     patientGenericPid: response.data.patientGenericPid,
     patientOriginalData: response.data.patientOriginalData,
     patientName: response.data.patientName,
     patientPhone: response.data.patientPhone,
     patientAddress: response.data.patientAddress,
     patientAge:
      response.data.patientAge !== undefined &&
      response.data.patientAge !== null &&
      response.data.patientAge !== 0
       ? response.data.patientAge
       : computedAge || 0,
     patientSex:
      response.data.patientSex || response.data.patientOriginalData?.sex || "",
     type: response.data.type,
     group: response.data.group,
     eye: response.data.eye,
     procedure: response.data.procedure,
     urgencyLevel: response.data.urgencyLevel,
     estimatedDuration: response.data.estimatedDuration,
     doctorId: response.data.doctorId,
     doctorName: response.data.doctorName,
     branchId: response.data.branchId,
     branchName: response.data.branchName,
     scheduledDate: scheduledDateFromApi,
     originalScheduledDate: response.data.originalScheduledDate,
     rescheduled: response.data.rescheduled || false,
     scheduledTime: scheduledTimeFromApi,
     timeFrame: response.data.timeFrame,
     slotCapacity: response.data.slotCapacity,
     slotBookedCount: response.data.slotBookedCount,
     dietPreference: response.data.dietPreference,
     bedPreference: response.data.bedPreference,
     isDiabetic: response.data.isDiabetic,
     additionalNotes: response.data.additionalNotes,
     allergies: response.data.allergies,
     currentMedications: response.data.currentMedications,
     whatsappNumber: response.data.whatsappNumber,
     enableWhatsappConfirmation: response.data.enableWhatsappConfirmation,
     enableEmailConfirmation: response.data.enableEmailConfirmation,
     bookingReference: response.data.bookingReference,
     status: response.data.status,
     createdById: response.data.createdById,
     createdByName: response.data.createdByName,
     createdAt: response.data.createdAt,
     updatedAt: response.data.updatedAt,
     advisedBy: response.data.advisedBy,
     advisedById: response.data.advisedById,
     // Anesthesia
     anesthesia: response.data.anesthesia,
     // Selected surgeries
     selectedSurgeries: response.data.selectedSurgeries,
     // Discharge template and notes
     selectedTemplateId: response.data.selectedTemplateId,
     selectedTemplateName: response.data.selectedTemplateName,
     selectedTemplateContent: response.data.selectedTemplateContent,
     // New discharge summary fields
     reasonForAdmission: response.data.reasonForAdmission,
     investigation: response.data.investigation,
     significantFindings: response.data.significantFindings,
     dischargeOtNotes: response.data.dischargeOtNotes,
     // Procedure performed display name for discharge summary
     procedureDischargeSummaryName: response.data.procedureDischargeSummaryName || "",
     // Old discharge fields (for backward compatibility)
     dischargeDiagnosis: response.data.dischargeDiagnosis,
     dischargeAdvice: response.data.dischargeAdvice,
     dischargeMedications: response.data.dischargeMedications,
     cancellationReason: response.data.cancellationReason,
    postponementNotes: response.data.postponementNotes || "",
     // Payments array for validation
     payments: response.data.payments || [],
     // Billing flags
     isBilled: !!response.data.isBilled,
     markedBilledAt: response.data.markedBilledAt || undefined,
     markedBilledBy: response.data.markedBilledBy || undefined,
     consolidatedBilling: response.data.consolidatedBilling || null,
    };

    // Initialize form data with API response
    const sxBooking = {
     // Patient Details
     patientId: response.data.patientId || "",
     patientGenericPid: response.data.patientGenericPid || "",
     patientName: response.data.patientName || "",
     patientPhone: response.data.patientPhone || "",
     patientAge:
      response.data.patientAge !== undefined &&
      response.data.patientAge !== null &&
      response.data.patientAge !== 0
       ? response.data.patientAge
       : computedAge || 0,
     patientSex:
      response.data.patientSex || response.data.patientOriginalData?.sex || "",

     // Surgery Details
     type: response.data.type || "",
     group: response.data.group || "",
     eye: response.data.eye || "left",
     procedure: response.data.procedure || "",
     urgencyLevel: response.data.urgencyLevel || "routine",
     estimatedDuration: response.data.estimatedDuration || 60,

     // Doctor & Branch
     doctorId: response.data.doctorId || "",
     doctorName: response.data.doctorName || "",
     branchId: response.data.branchId || "",
     branchName: response.data.branchName || "",

     // Schedule
     date: scheduledDateFromApi ? new Date(scheduledDateFromApi) : null,
     originalScheduledDate: response.data.originalScheduledDate,
     rescheduled: response.data.rescheduled || false,
     time: this.parseTimeFromAPI(scheduledTimeFromApi),
     timeFrame: response.data.timeFrame || "",
     otTheatre: response.data.otTheatre || "",
     slotCapacity: response.data.slotCapacity || 0,
     slotBookedCount: response.data.slotBookedCount || 0,

     // Medical Details
     dietPreference: response.data.dietPreference || "regular",
     bedPreference: response.data.bedPreference || "general",
     isDiabetic: response.data.isDiabetic || false,
     additionalNotes: response.data.additionalNotes || "",
     allergies: response.data.allergies || "",
     currentMedications: response.data.currentMedications || "",

     // Contact & Communication
     whatsappNumber: response.data.whatsappNumber || "",
     enableWhatsappConfirmation:
      response.data.enableWhatsappConfirmation || true,
     enableEmailConfirmation: response.data.enableEmailConfirmation || true,

     // Booking Details
     bookingReference: response.data.bookingReference || "",
     status: response.data.status || "confirmed",

     // Additional form fields
     surgeon: response.data.doctorName || response.data.surgeon || "",
     surgeonId: response.data.doctorId || response.data.surgeonId || "",
     secondarySurgeon: response.data.secondarySurgeon || "",
     secondarySurgeonId: response.data.secondarySurgeonId || "",
     anaesthetist: response.data.anaesthetist || "",
     anaesthetistId: response.data.anaesthetistId || "",
     advisedBy: response.data.advisedBy || "",
     advisedById: response.data.advisedById || "",
     chosenBy: response.data.chosenBy || "",
     room: response.data.room || "",
     laterality: response.data.laterality || "left",
     diet: response.data.diet || response.data.dietPreference || "regular",
     diabetic: response.data.diabetic || response.data.isDiabetic || false,
     anesthesia: response.data.anesthesia || "", // Overall anesthesia for all surgeries
     surgeries: (response.data.selectedSurgeries || []).map((surgery) => ({
      ...surgery,
      printedName: surgery.name || surgery.printedName || "Unknown Surgery",
      group: surgery.group || surgery.payor || "General",
      eyeMapping: surgery.eyeMapping || "N/A",
      eye: surgery.laterality || surgery.eye || "",
      laterality: surgery.laterality || surgery.eye || "",
      // anesthesia removed from individual surgeries - now at overall level
     })),
     notes: response.data.additionalNotes || response.data.notes || "",
    postponementNotes: response.data.postponementNotes || "",
     // IOL items with variants
     IOLitems: response.data.IOLitems || [],
     // Payor panel (used for filtering surgeries for addition)
     payorPanel: response.data.payorPanel || "",
     cashSubsidy: response.data.cashSubsidy || "",
     // Discharge summary
     procedureDischargeSummaryName: response.data.procedureDischargeSummaryName || "",
     // Billing flags (mirror)
     isBilled: !!response.data.isBilled,
     markedBilledAt: response.data.markedBilledAt || undefined,
     markedBilledBy: response.data.markedBilledBy || undefined,
    };

    this.setState(
        {
            surgery,
            sxBooking,
            originalSxBooking: JSON.parse(JSON.stringify(sxBooking)), // Deep copy for comparison
            systemicInvestigations: response.data.systemicInvestigations || [],
            keyPeople: response.data.keyPeople || [],
            keyEvents: response.data.keyEvents || [],
            // Load consumptions directly from booking response if available
            itemConsumptions: this.mergeConsumptions(
                this.state?.itemConsumptions || [],
                Array.isArray(response.data.consumptionItems)
                    ? response.data.consumptionItems
                    : []
            ),
            isLoading: false,
            // Reset dirty states when loading fresh data
            isMedicalTeamDirty: false,
            isScheduleDirty: false,
            isClinicalDetailsDirty: false,
            isNotesDirty: false,
            // Always re-check payment/refund status on open before enabling cancellation
            isCheckingCancelEligibility: true,
            cancelEligibilityError: null,
        },
        () => {
            this.refreshCancelEligibilityForLoadedSurgery(surgery.id);
        }
    );

    // Load files related to this surgery booking
    this.loadFiles();
    // If booking response did not include consumptions, fallback to dedicated API (merge, don't overwrite)
    if (
     !Array.isArray(response.data.consumptionItems) ||
     response.data.consumptionItems.length === 0
    ) {
     this.loadItemConsumption();
    }
   } else {
    throw new Error(response.message || "Failed to load surgery details");
   }
  } catch (err) {
   console.error("Error loading surgery details:", err);
   this.setState({
    error: err.message || "Failed to load surgery details",
    isLoading: false,
   });
  }
 };

 // Load consultants when dropdown is clicked
 loadConsultants = async () => {
  try {
   this.setState({ isLoadingConsultants: true });
   if (window.__neoCache?.consultants) {
    const mapped = (window.__neoCache.consultants || []).map((consultant) => ({
     id: consultant._id || consultant.id,
     name: consultant.name || consultant.consultantName,
     specialization: consultant.specialization || consultant.department,
     phone: consultant.phone || consultant.contactNumber,
     email: consultant.email || consultant.emailId,
     shortcode: consultant.shortcode || consultant.code,
    }));
    this.setState({ consultants: mapped, isLoadingConsultants: false });
    return;
   }
   if (window.__neoInflight?.getAllConsultants) {
    const inflight = await window.__neoInflight.getAllConsultants;
    const response = inflight;
    if (response && Array.isArray(response)) {
     const consultants = response.map((consultant) => ({
      id: consultant._id || consultant.id,
      name: consultant.name || consultant.consultantName,
      specialization: consultant.specialization || consultant.department,
      phone: consultant.phone || consultant.contactNumber,
      email: consultant.email || consultant.emailId,
      shortcode: consultant.shortcode || consultant.code,
     }));
     this.setState({ consultants: consultants, isLoadingConsultants: false });
     return;
    }
   }

   window.__neoInflight.getAllConsultants = apiCallPost(
    `/rai/${this.props.authStore.prefix}/getAllConsultants`,
    {}
   );
   const response = await window.__neoInflight.getAllConsultants;

   if (response && Array.isArray(response)) {
    const consultants = response.map((consultant) => ({
     id: consultant._id || consultant.id,
     name: consultant.name || consultant.consultantName,
     specialization: consultant.specialization || consultant.department,
     phone: consultant.phone || consultant.contactNumber,
     email: consultant.email || consultant.emailId,
     shortcode: consultant.shortcode || consultant.code,
    }));

    this.setState({
     consultants: consultants,
     isLoadingConsultants: false,
    });
   } else {
    throw new Error("Failed to load consultants");
   }
  } catch (err) {
   console.error("Error loading consultants:", err);
   cogoToast.error("Failed to load consultant list");
   this.setState({ isLoadingConsultants: false });
  }
 };

 // Load anesthesia types for Clinical Details dropdown
 loadAnesthesiaTypes = async () => {
  try {
   this.setState({ isLoadingAnesthesiaTypes: true });
   if (window.__neoCache?.anesthesiaOptionsForSurgery) {
    const options = window.__neoCache.anesthesiaOptionsForSurgery;
    const names = options.map((o) => o.label);
    this.setState({
     anesthesiaTypes: names,
     anesthesiaOptions: options,
     isLoadingAnesthesiaTypes: false,
    });
    return;
   }
   if (window.__neoInflight?.getAnesthesiaForSurgery) {
    const response = await window.__neoInflight.getAnesthesiaForSurgery;
    const items = Array.isArray(response?.data)
     ? response.data
     : Array.isArray(response)
     ? response
     : [];
    const options = items
     .filter(
      (item) =>
       item && typeof item.name === "string" && item.name.trim().length > 0
     )
     .map((item) => ({
      value: item.name,
      label: item.name,
      rate: item.rate || 0,
     }));
    const names = options.map((o) => o.label);
    this.setState({
     anesthesiaTypes: names,
     anesthesiaOptions: options,
     isLoadingAnesthesiaTypes: false,
    });
    if (typeof window !== "undefined")
     window.__neoCache.anesthesiaOptionsForSurgery = options;
    return;
   }
   window.__neoInflight.getAnesthesiaForSurgery = apiCallPost(
    `/v3/${this.props.authStore.prefix}/neoSxAnesthesiaPaymentMaster/getAll`,
    {}
   );
   const response = await window.__neoInflight.getAnesthesiaForSurgery;

   const items = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
    ? response
    : [];
   const options = items
    .filter(
     (item) =>
      item && typeof item.name === "string" && item.name.trim().length > 0
    )
    .map((item) => ({
     value: item.name,
     label: item.name,
     rate: item.rate || 0,
    }));
   const names = options.map((o) => o.label);

   this.setState({
    anesthesiaTypes: names,
    anesthesiaOptions: options,
    isLoadingAnesthesiaTypes: false,
   });
   if (typeof window !== "undefined")
    window.__neoCache.anesthesiaOptionsForSurgery = options;

   console.log(
    "✅ DEBUG: Anesthesia types loaded successfully:",
    options.length
   );
  } catch (err) {
   console.error("Error loading anesthesia types:", err);
   // Fallback to dummy anesthesia types
   const fallbackOptions = [
    { value: "General Anesthesia", label: "General Anesthesia", rate: 0 },
    { value: "Local Anesthesia", label: "Local Anesthesia", rate: 0 },
    { value: "Topical Anesthesia", label: "Topical Anesthesia", rate: 0 },
    { value: "Regional Anesthesia", label: "Regional Anesthesia", rate: 0 },
   ];
   this.setState({
    anesthesiaTypes: fallbackOptions.map((o) => o.label),
    anesthesiaOptions: fallbackOptions,
    isLoadingAnesthesiaTypes: false,
   });
   console.warn("⚠️ DEBUG: Using fallback anesthesia types due to API error");
  }
 };

 // Load item consumption data using dedicated endpoint (avoid extra getById)
 loadItemConsumption = async () => {
  try {
   if (this.state.surgery?.id) {
    if (!this.isValidObjectId(this.state.surgery.id)) {
     console.error(
      "Invalid surgery ID format for item consumption:",
      this.state.surgery.id
     );
     return;
    }

    const response = await apiCallPost(
     `/v3/${this.props.authStore.prefix}/sxItemConsumption/getBySurgeryBooking`,
     {
      surgeryBookingId: this.state.surgery.id,
     }
    );

    console.log("loadItemConsumption response:", response);

    if (response?.success) {
     const incoming = Array.isArray(response.data) ? response.data : [];
     console.log("Loaded item consumptions:", incoming);
     console.log("Number of consumptions loaded:", incoming.length);

     // Replace instead of merge to ensure clean data
     this.setState(
      {
       itemConsumptions: incoming,
      },
      () => {
       console.log(
        "State updated, itemConsumptions now:",
        this.state.itemConsumptions
       );
      }
     );

     return incoming; // Return the data for await callers
    } else {
     console.warn("Item consumption load failed or empty:", response);
     return [];
    }
   }
  } catch (err) {
   if (err?.message && err.message.includes("ObjectID")) {
    console.error("Invalid surgery ID for item consumption:", err);
   } else {
    console.error("Error loading item consumption:", err);
   }
   return [];
  }
 };

 // Files: load
 loadFiles = async () => {
  try {
   if (!this.state.surgery?.id) return;

   // Validate ObjectID format before making API call
   if (!this.isValidObjectId(this.state.surgery.id)) {
    console.error(
     "Invalid surgery ID format for file loading:",
     this.state.surgery.id
    );
    this.setState({ isLoadingFiles: false });
    return;
   }

   this.setState({ isLoadingFiles: true });
   const res = await apiCallPost(
    `/v3/${this.props.authStore.prefix}/neoFiles/getByAssociatedEntity`,
    {
     associatedEntityId: this.state.surgery.id,
    }
   );
   this.setState({
    files: Array.isArray(res) ? res : [],
    isLoadingFiles: false,
   });
  } catch (err) {
   this.setState({ isLoadingFiles: false });

   // Provide more specific error messages
   if (err.message && err.message.includes("ObjectID")) {
    cogoToast.error("Invalid surgery ID. Unable to load files.");
   } else {
    cogoToast.error("Failed to load files");
   }

   console.error("Load files error:", err);
  }
 };

 // Files: upload
 uploadFile = async () => {
  if (!this.state.newFileBlob) {
   cogoToast.error("Please choose a file");
   return;
  }

  // Validate surgery data and ID
  if (!this.state.surgery || !this.state.surgery.id) {
   cogoToast.error(
    "Surgery data not loaded. Please refresh the page and try again."
   );
   return;
  }

  if (!this.isValidObjectId(this.state.surgery.id)) {
   cogoToast.error("Invalid surgery ID. Unable to upload file.");
   console.error("Invalid surgery ID format:", this.state.surgery.id);
   return;
  }

  try {
   this.setState({
    isUploadingFile: true,
    uploadProgressDone: 0,
    uploadProgressTotal: 1,
   });
   const fd = new FormData();
   fd.append("associatedEntityId", this.state.surgery.id);
   fd.append("identifier", this.state.newFileBlob.name || "uploaded-file");
   fd.append("file", this.state.newFileBlob);

   await apiCallPostMultipartProgress(
    `/v3/${this.props.authStore.prefix}/neoFiles/uploadNew`,
    fd,
    (done, total) =>
     this.setState({
      uploadProgressDone: done,
      uploadProgressTotal: total || 1,
     })
   );

   cogoToast.success("File uploaded successfully");
   this.setState({ isUploadingFile: false, newFileBlob: null }, this.loadFiles);
  } catch (err) {
   this.setState({ isUploadingFile: false });

   // Provide more specific error messages
   if (err.message && err.message.includes("ObjectID")) {
    cogoToast.error("Invalid surgery ID format. Please contact support.");
   } else if (err.message && err.message.includes("File too large")) {
    cogoToast.error("File is too large. Please choose a smaller file.");
   } else {
    cogoToast.error("Upload failed. Please try again.");
   }

   console.error("File upload error:", err);
  }
 };

 // Files: delete
 deleteFile = async (file) => {
  try {
   await apiCallPost(`/v3/${this.props.authStore.prefix}/neoFiles/delete`, {
    _id: file._id,
   });
   cogoToast.success("File deleted");
   this.loadFiles();
  } catch (err) {
   cogoToast.error("Failed to delete file");
   console.error(err);
  }
 };

 // Theme helper for Blueprint dialogs
 getThemeClass = () => {
  try {
   const theme = document?.documentElement?.getAttribute("data-theme");
   return theme === "dark" ? "bp3-dark" : "";
  } catch (e) {
   return "";
  }
 };

 // ----- Status helpers (centralized) -----
 getStatusMeta = (status) => getStatusMetaGlobal(status);
 getDisplayStatusMeta = (status, payorPanel, isBilled) =>
  getDisplayStatusMetaGlobal({ status, payorPanel, isBilled });

 // Receive updates from BillingTab (supports object payload for billed flags)
 handleStatusChangeFromBilling = (update) => {
  try {
   const surgeryId = this.state.surgery?.id;
   if (update && typeof update === "object") {
    const billingPatch = {};
    if ("isBilled" in update && typeof update.isBilled === "boolean") {
     billingPatch.isBilled = update.isBilled;
    }
    if ("markedBilledAt" in update) {
     billingPatch.markedBilledAt = update.markedBilledAt;
    }
    if ("markedBilledBy" in update) {
     billingPatch.markedBilledBy = update.markedBilledBy;
    }
    const nextIsBilled =
     "isBilled" in billingPatch ? billingPatch.isBilled : undefined;
    this.setState((prev) => ({
     surgery: { ...(prev.surgery || {}), ...billingPatch },
     sxBooking: { ...(prev.sxBooking || {}), ...billingPatch },
     isEditingDetails:
      nextIsBilled === true ? false : prev.isEditingDetails,
    }));
    try {
     if (surgeryId && window.__neoCache?.bookingById?.[surgeryId]) {
      window.__neoCache.bookingById[surgeryId] = {
       ...window.__neoCache.bookingById[surgeryId],
       ...billingPatch,
      };
     }
    } catch (e) {}
    return;
   }
   // Backward compatibility: original string status
   const status = String(update || "");
   this.setState((prev) => ({
    surgery: {
     ...(prev.surgery || {}),
     status,
     isBilled:
      status.toLowerCase() === "billed" ? true : prev.surgery?.isBilled,
    },
    sxBooking: {
     ...(prev.sxBooking || {}),
     status,
     isBilled:
      status.toLowerCase() === "billed" ? true : prev.sxBooking?.isBilled,
    },
    isEditingDetails:
     status.toLowerCase() === "billed" ? false : prev.isEditingDetails,
   }));
   try {
    if (surgeryId && window.__neoCache?.bookingById?.[surgeryId]) {
     window.__neoCache.bookingById[surgeryId] = {
      ...window.__neoCache.bookingById[surgeryId],
      status,
      isBilled:
       status.toLowerCase() === "billed"
        ? true
        : window.__neoCache.bookingById[surgeryId]?.isBilled,
     };
    }
   } catch (e) {}
  } catch (e) {}
 };

 handleBillingDataPatchFromTab = (patch = {}) => {
  if (!patch || typeof patch !== "object") return;
  const shouldCheckPayments = Object.prototype.hasOwnProperty.call(
   patch,
   "payments"
  );
  const shouldCheckAuthorization = Object.prototype.hasOwnProperty.call(
   patch,
   "authorization"
  );
  const shouldCheckConsolidated = Object.prototype.hasOwnProperty.call(
   patch,
   "consolidatedBilling"
  );
  const shouldRunBalanceCheck =
   shouldCheckPayments || shouldCheckAuthorization || shouldCheckConsolidated;
  this.setState(
   (prev) => {
    const nextState = {
     surgery: { ...(prev.surgery || {}), ...patch },
    };
    const sxKeys = [
     "isBilled",
     "markedBilledAt",
     "markedBilledBy",
     "anesthesia",
     "consolidatedBilling",
    ];
    const sxPatch = {};
    sxKeys.forEach((key) => {
     if (Object.prototype.hasOwnProperty.call(patch, key)) {
      sxPatch[key] = patch[key];
     }
    });
    if (Object.keys(sxPatch).length > 0) {
     nextState.sxBooking = { ...(prev.sxBooking || {}), ...sxPatch };
    }
    return nextState;
   },
   () => {
    try {
     const surgeryId = this.state.surgery?.id;
     if (surgeryId && window.__neoCache?.bookingById?.[surgeryId]) {
      window.__neoCache.bookingById[surgeryId] = {
       ...window.__neoCache.bookingById[surgeryId],
       ...patch,
      };
     }
    } catch (e) {}
   }
  );
 };

 // Utility: copy value to clipboard with toast feedback
 copyToClipboard = async (value) => {
  try {
   if (!value) {
    cogoToast.warn("Nothing to copy");
    return;
   }

   if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(String(value));
    cogoToast.success("Copied to clipboard");
    return;
   }

   // Fallback for older browsers
   const textarea = document.createElement("textarea");
   textarea.value = String(value);
   textarea.setAttribute("readonly", "");
   textarea.style.position = "absolute";
   textarea.style.left = "-9999px";
   document.body.appendChild(textarea);
   textarea.select();
   const successful = document.execCommand("copy");
   document.body.removeChild(textarea);
   if (successful) {
    cogoToast.success("Copied to clipboard");
   } else {
    throw new Error("execCommand copy failed");
   }
  } catch (err) {
   cogoToast.error("Failed to copy");
   console.error("Copy to clipboard failed:", err);
  }
 };

 // Navigation methods
 goBack = () => {
  this.props.history.goBack();
 };

 goToSurgeryList = () => {
  this.props.history.push("/v3/surgery-booking");
 };

 // Tab change handler
 handleTabChange = (newTabId) => {
  console.log("Tab changed to:", newTabId);
  this.setState((prevState) => ({
   activeTab: newTabId,
   auditRefreshTick:
    newTabId === "audit"
     ? (prevState.auditRefreshTick || 0) + 1
     : prevState.auditRefreshTick,
  }));

  // When switching to Consumption tab, ensure locations are preloaded
  if (newTabId === "consumption") {
   const shouldLoadLocations =
    !this.state.isLoadingLocations &&
    (this.state.availableLocations || []).length === 0;
   if (shouldLoadLocations) {
    this.loadLocations();
   }
  }

  if (newTabId === "discharge") {
   this.ensureProcedureSummaryForCurrentSurgery();
  }

  // When switching to Billing tab, ensure anesthesia options are loaded and billing data is refreshed
  if (newTabId === "billing") {
   // Force a small delay to ensure the BillingTab component is mounted before triggering updates
   setTimeout(() => {
    this.forceUpdate(); // This will trigger componentDidUpdate in BillingTab
   }, 100);

   // Also trigger anesthesia refresh after a bit more delay
   setTimeout(() => {
    if (this.billingTabRef && this.billingTabRef.current) {
     this.billingTabRef.current.refreshAnesthesia();
    }
   }, 300);
  }
 };

 // SX Booking Tab Methods
 updateSxBookingField = (field, value, callback) => {
  this.setState(
   (prevState) => {
    const updatedSxBooking = {
     ...prevState.sxBooking,
     [field]: value,
    };

    const surgeryPatch = {};
    if (field === "date") {
     surgeryPatch.scheduledDate = value || null;
    }
    if (field === "time") {
     surgeryPatch.scheduledTime = this.formatScheduleTime(value);
    }
    if (field === "status") {
     surgeryPatch.status = value || "";
    }
    if (field === "procedureDischargeSummaryName") {
     surgeryPatch.procedureDischargeSummaryName = value || "";
    }

    const nextState = { sxBooking: updatedSxBooking };
    if (Object.keys(surgeryPatch).length > 0) {
     nextState.surgery = {
      ...(prevState.surgery || {}),
      ...surgeryPatch,
     };
    }
    return nextState;
   },
   () => {
    // Check for changes after state update
    this.checkForChanges();
    if (typeof callback === "function") {
     try {
      callback();
     } catch (e) {
      console.error("updateSxBookingField callback error:", e);
     }
    }
   }
  );
 };

 // Check for changes in each section
 checkForChanges = () => {
  if (!this.state.originalSxBooking) return;

  const current = this.state.sxBooking;
  const original = this.state.originalSxBooking;

  // Check Medical Team changes
  const medicalTeamChanged =
   current.surgeonId !== original.surgeonId ||
   current.surgeon !== original.surgeon ||
   current.secondarySurgeonId !== original.secondarySurgeonId ||
   current.secondarySurgeon !== original.secondarySurgeon ||
   current.anaesthetistId !== original.anaesthetistId ||
   current.anaesthetist !== original.anaesthetist ||
   current.advisedById !== original.advisedById ||
   current.advisedBy !== original.advisedBy ||
   current.chosenBy !== original.chosenBy;

  // Check Schedule changes - compare dates and times properly
  const dateChanged =
   current.date && original.date
    ? moment(current.date).format("YYYY-MM-DD") !==
      moment(original.date).format("YYYY-MM-DD")
    : current.date !== original.date;

  const timeChanged = (() => {
   const normalizeTime = (t) => {
    if (!t) return "";
    if (t instanceof Date) return moment(t).format("HH:mm");
    if (typeof t === "string") {
     // Try to parse common formats and normalize to HH:mm
     const m = moment(
      t,
      [moment.ISO_8601, "HH:mm", "H:mm", "h:mm A", "hh:mm A"],
      true
     );
     if (m.isValid()) return m.format("HH:mm");
     // Fallback: best-effort parse then format, else return trimmed string
     const m2 = moment(t);
     return m2.isValid() ? m2.format("HH:mm") : String(t).trim();
    }
    return "";
   };
   return normalizeTime(current.time) !== normalizeTime(original.time);
  })();

  const scheduleChanged =
   dateChanged ||
   timeChanged ||
   current.otTheatre !== original.otTheatre ||
   current.room !== original.room;

  // Check Clinical Details changes (including anesthesia moved from per-surgery to overall)
  const clinicalDetailsChanged =
   current.diet !== original.diet ||
   current.diabetic !== original.diabetic ||
   current.anesthesia !== original.anesthesia;

  // Check Notes changes separately
  const notesChanged = current.notes !== original.notes;

  this.setState({
   isMedicalTeamDirty: medicalTeamChanged,
   isScheduleDirty: scheduleChanged,
   isClinicalDetailsDirty: clinicalDetailsChanged,
   isNotesDirty: notesChanged,
  });
 };

 handleSurgeonDropdownClick = () => {
  if (this.state.consultants.length === 0 && !this.state.isLoadingConsultants) {
   this.loadConsultants();
  }
 };

 handleSurgeonChange = (consultantId) => {
  return new Promise((resolve) => {
   if (!consultantId) {
    this.setState(
     (prevState) => ({
      sxBooking: {
       ...prevState.sxBooking,
       surgeon: "",
       surgeonId: "",
      },
     }),
     () => {
      this.checkForChanges();
      resolve();
     }
    );
    return;
   }
   const selectedConsultant = this.state.consultants.find(
    (consultant) => consultant.id === consultantId
   );
   if (selectedConsultant) {
    this.setState(
     (prevState) => ({
      sxBooking: {
       ...prevState.sxBooking,
       surgeon: selectedConsultant.name,
       surgeonId: selectedConsultant.id,
      },
     }),
     () => {
      this.checkForChanges();
      resolve();
     }
    );
   } else {
    resolve();
   }
  });
 };

 handleSecondarySurgeonChange = (consultantId) => {
  return new Promise((resolve) => {
   if (!consultantId) {
    this.setState(
     (prevState) => ({
      sxBooking: {
       ...prevState.sxBooking,
       secondarySurgeon: "",
       secondarySurgeonId: "",
      },
     }),
     () => {
      this.checkForChanges();
      resolve();
     }
    );
    return;
   }
   const selectedConsultant = this.state.consultants.find(
    (consultant) => consultant.id === consultantId
   );
   if (selectedConsultant) {
    this.setState(
     (prevState) => ({
      sxBooking: {
       ...prevState.sxBooking,
       secondarySurgeon: selectedConsultant.name,
       secondarySurgeonId: selectedConsultant.id,
      },
     }),
     () => {
      this.checkForChanges();
      resolve();
     }
    );
   } else {
    resolve();
   }
  });
 };

 handleAnaesthetistChange = (consultantId) => {
  return new Promise((resolve) => {
   if (!consultantId) {
    this.setState(
     (prevState) => ({
      sxBooking: {
       ...prevState.sxBooking,
       anaesthetist: "",
       anaesthetistId: "",
      },
     }),
     () => {
      this.checkForChanges();
      resolve();
     }
    );
    return;
   }
   const selectedConsultant = this.state.consultants.find(
    (consultant) => consultant.id === consultantId
   );
   if (selectedConsultant) {
    this.setState(
     (prevState) => ({
      sxBooking: {
       ...prevState.sxBooking,
       anaesthetist: selectedConsultant.name,
       anaesthetistId: selectedConsultant.id,
      },
     }),
     () => {
      this.checkForChanges();
      resolve();
     }
    );
   } else {
    resolve();
   }
  });
 };

 handleAdvisedByChange = (consultantId) => {
  const selectedConsultant = this.state.consultants.find(
   (consultant) => consultant.id === consultantId
  );
  // Allow clearing the field
  if (!consultantId) {
   this.setState(
    (prevState) => ({
     sxBooking: {
      ...prevState.sxBooking,
      advisedBy: "",
      advisedById: "",
     },
    }),
    () => {
     this.checkForChanges();
    }
   );
   return;
  }
  if (selectedConsultant) {
   this.setState(
    (prevState) => ({
     sxBooking: {
      ...prevState.sxBooking,
      advisedBy: selectedConsultant.name,
      advisedById: selectedConsultant.id,
     },
    }),
    () => {
     this.checkForChanges();
    }
   );
  }
 };

 clearMedicalTeamField = async (field) => {
  try {
   // Capture previous values before clearing for audit logging
   const previousValues = {
    secondarySurgeon: {
     name: this.state.sxBooking.secondarySurgeon || "",
     id: this.state.sxBooking.secondarySurgeonId || "",
     label: "Secondary Surgeon",
     fieldName: "secondarySurgeon",
    },
    anaesthetist: {
     name: this.state.sxBooking.anaesthetist || "",
     id: this.state.sxBooking.anaesthetistId || "",
     label: "Anaesthetist",
     fieldName: "anaesthetist",
    },
    advisedBy: {
     name: this.state.sxBooking.advisedBy || "",
     id: this.state.sxBooking.advisedById || "",
     label: "Advised By",
     fieldName: "advisedBy",
    },
    surgeon: {
     name: this.state.sxBooking.surgeon || "",
     id: this.state.sxBooking.surgeonId || "",
     label: "Surgeon",
     fieldName: "surgeon",
    },
   };
   const prevInfo = previousValues[field];

   const updates = {};
   switch (field) {
    case "surgeon":
     updates.surgeon = "";
     updates.surgeonId = "";
     break;
    case "secondarySurgeon":
     updates.secondarySurgeon = "";
     updates.secondarySurgeonId = "";
     break;
    case "anaesthetist":
     updates.anaesthetist = "";
     updates.anaesthetistId = "";
     break;
    case "advisedBy":
     updates.advisedBy = "";
     updates.advisedById = "";
     break;
    default:
     return;
   }
   await new Promise((resolve) => {
    this.setState(
     (prevState) => ({
      sxBooking: {
       ...prevState.sxBooking,
       ...updates,
      },
     }),
     () => {
      this.checkForChanges();
      resolve();
     }
    );
   });
   await this.saveMedicalTeam();

   // Create audit log entry for the removal
   if (prevInfo && prevInfo.name) {
    try {
     const resourceId = this.state.surgery?.id;
     const resourceName = `Surgery - ${
      this.state.surgery?.patientName || "Unknown Patient"
     }`;
     if (resourceId) {
      await apiCallPost(
       `/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`,
       [
        {
         resourceType: "SURGERY_BOOKING",
         resourceId,
         resourceName,
         actionType: "UPDATE",
         actionSlug: `Removed ${prevInfo.label}: "${prevInfo.name}"`,
         actionMetadata: {
          fieldName: prevInfo.fieldName,
          changeType: "REMOVE",
          previousValue: prevInfo.name,
          newValue: "",
         },
        },
       ]
      );
     }
    } catch (auditErr) {
     console.error("Audit log failed for clearing medical team field:", auditErr);
    }
   }
  } catch (err) {
   console.error("Error clearing medical team field:", err);
   cogoToast.error("Failed to clear selection");
  }
 };

 isDischargedStatus = () => {
  const status = (
   this.state.surgery?.status ||
   this.state.sxBooking?.status ||
   ""
  )
   .toString()
   .toLowerCase();
  return status === "discharged";
 };

 isCancelledStatus = () => {
  const status = (
   this.state.surgery?.status ||
   this.state.sxBooking?.status ||
   ""
  )
   .toString()
   .toLowerCase();
  return status === "cancelled";
 };

 hasSxBillingSuperuserAccess = () => isSxBillingSuperuser(this.props.authStore);

 // Billing admins (User Access list) keep edit rights on billed bookings;
 // after discharge billing is read-only for everyone until an admin sets the
 // status back to Confirmed.
 getBillingLockReason = () => {
  if (this.isCancelledStatus()) {
   return "Billing is locked because this surgery has been cancelled.";
  }
  if (this.isDischargedStatus()) {
   return "Billing is locked because this surgery has been discharged. To change amounts, refunds or authorizations, raise a ticket to set the status back to Confirmed.";
  }
  const billed = Boolean(this.state.surgery?.isBilled || this.state.sxBooking?.isBilled);
  if (billed && !this.hasSxBillingSuperuserAccess()) {
   return "Consolidated billing and tools are locked because this surgery is marked as billed. Payments and history stay available. Changing anaesthesia or adding/removing a procedure will unbill it and unlock billing.";
  }
  return null;
 };

 /** Payor panel can be changed anytime for designated support logins (PATCH + audit). */
 applyPayorPanelOverride = async (panelName) => {
  const surgeryId = this.state.surgery?.id;
  const prefix = this.props.authStore?.prefix;
  if (!surgeryId || !prefix) {
   cogoToast.error("Booking not loaded");
   return;
  }
  if (!this.hasSxBillingSuperuserAccess()) {
   cogoToast.warn("You do not have permission to change payor panel here");
   return;
  }
  const trimmed = String(panelName || "").trim();
  const prev = this.getCurrentPayorPanel();
  if (trimmed === prev) return;
  const { ok } = await this.ensureFreshBeforeAction();
  if (!ok) return;
  this.setState({ isSavingPayorPanelOverride: true });
  try {
   const res = await apiCallPatch(
    `/v3/${prefix}/neoSxBookingMaster/update/${surgeryId}`,
    { payorPanel: trimmed }
   );
   if (!res?.success) {
    throw new Error(res?.message || "Update failed");
   }
   try {
    await apiCallPost(`/v3/${prefix}/neoSxAuditLog/createBulk`, [
     {
      resourceType: "SURGERY_BOOKING",
      resourceId: surgeryId,
      resourceName: `Surgery - ${this.state.surgery?.patientName || "Unknown Patient"}`,
      actionType: "UPDATE",
      actionSlug: `Payor panel changed (billing admin): "${prev || "None"}" → "${trimmed || "None"}"`,
      actionMetadata: {
       fieldName: "payorPanel",
       changeType: "UPDATE",
       previousValue: prev,
       newValue: trimmed,
       billingAdminOverride: true,
      },
     },
    ]);
   } catch (auditErr) {
    console.error("Audit log failed for payor panel override:", auditErr);
   }
   this.setState((prevState) => ({
    sxBooking: { ...prevState.sxBooking, payorPanel: trimmed },
    surgery: prevState.surgery
     ? { ...prevState.surgery, payorPanel: trimmed }
     : prevState.surgery,
    originalSxBooking: prevState.originalSxBooking
     ? { ...prevState.originalSxBooking, payorPanel: trimmed }
     : prevState.originalSxBooking,
   }));
   cogoToast.success("Payor panel updated");

   // A payor-panel change can alter the authorized amount and leave an
   // outstanding balance. Reconcile the billed status so a booking that now
   // carries a balance is flipped back to Unbilled instead of staying Billed.
   try {
    await this.ensureUnbilledIfBalanceNonZero("payor-panel-change");
   } catch (unbillErr) {
    console.error(
     "Auto-unbill after payor panel change failed:",
     unbillErr
    );
   }
   // Billing rules (authorization vs cash) depend on the panel, so reload
   // every tab from the server rather than keep panel-specific local state.
   this.reloadForServerChange([], {
    message: `Payor panel changed to "${trimmed || "-"}". Reloading so billing uses the new panel.`,
   });
  } catch (err) {
   cogoToast.error("Failed to update payor panel");
   console.error(err);
  } finally {
   this.setState({ isSavingPayorPanelOverride: false });
  }
 };

 getCurrentPayorPanel = () => {
  const surgeryPayor =
   (this.state.surgery?.selectedSurgeries || this.state.surgery?.surgeries || [])[0]
    ?.payor;
  const sxBookingPayor =
   (this.state.sxBooking?.selectedSurgeries ||
    this.state.sxBooking?.surgeries ||
    [])[0]?.payor;

  return (
   this.state.sxBooking?.payorPanel ||
   this.state.surgery?.payorPanel ||
   surgeryPayor ||
   sxBookingPayor ||
   ""
  );
 };

 isCashPayorPanel = () =>
  String(this.getCurrentPayorPanel() || "")
   .trim()
   .toLowerCase() === "cash";

 refreshPayorPanelFromServer = async () => {
  try {
   const surgeryId = this.state.surgery?.id;
   const prefix = this.props.authStore?.prefix;
   if (!surgeryId || !prefix) return { changed: false, newPayorPanel: "" };

   const res = await apiCallGet(
    `/v3/${prefix}/neoSxBookingMaster/getById/${surgeryId}?_ts=${Date.now()}`
   );
   if (!res?.success || !res?.data) return { changed: false, newPayorPanel: "" };

   const fresh = res.data;
   const serverPayorPanel = fresh.payorPanel || "";
   const localPayorPanel = this.getCurrentPayorPanel();
   const changed =
    serverPayorPanel.trim().toLowerCase() !==
    localPayorPanel.trim().toLowerCase();

   if (changed) {
    this.setState((prev) => ({
     surgery: prev.surgery ? { ...prev.surgery, ...fresh } : prev.surgery,
     sxBooking: prev.sxBooking
      ? { ...prev.sxBooking, ...fresh }
      : prev.sxBooking,
    }));

    try {
     if (typeof window !== "undefined") {
      window.__neoCache = window.__neoCache || {};
      window.__neoCache.bookingById = window.__neoCache.bookingById || {};
      window.__neoCache.bookingById[surgeryId] = fresh;
     }
    } catch (e) {}
   }

   return { changed, newPayorPanel: serverPayorPanel };
  } catch (err) {
   console.error("Failed to refresh payor panel from server:", err);
   return { changed: false, newPayorPanel: "" };
  }
 };

 getViewDischargeDisabledReason = () => {
  const status = (
   this.state.surgery?.status ||
   this.state.sxBooking?.status ||
   ""
  )
   .toString()
   .toLowerCase();

  if (status === "postponed") {
   return "Discharge summary is unavailable while surgery is postponed";
  }

  if (this.isCashPayorPanel() && status !== "discharged") {
   return "For CASH payor panel, discharge summary is available only after Mark Discharged";
  }

  return "";
 };

 addSurgery = () => {
  if (this.isDischargedStatus() || this.isCancelledStatus()) {
   const statusLabel = this.isCancelledStatus() ? "Cancellation" : "Discharge";
   cogoToast.warn(`Cannot add surgeries after ${statusLabel}`);
   return;
  }
  // Open dialog and load printed names if not already loaded
  this.setState({ showAddSurgeryDialog: true }, () => {
   if (
    (this.state.printedNames || []).length === 0 &&
    !this.state.isLoadingPrintedNames
   ) {
    this.loadPrintedNames();
   }
  });
 };

 removeSurgery = (index) => {
  if (this.isDischargedStatus() || this.isCancelledStatus()) {
   const statusLabel = this.isCancelledStatus() ? "Cancellation" : "Discharge";
   cogoToast.warn(`Cannot remove surgeries after ${statusLabel}`);
   return;
  }
  const surgeries = this.state.sxBooking?.surgeries || [];
  if (surgeries.length <= 1) {
   cogoToast.warn(
    "Cannot delete the only surgery. Please add another surgery first."
   );
   return;
  }

  const newSurgeries = surgeries.filter((_, i) => i !== index);
  this.updateSelectedSurgeriesOnServer(newSurgeries, {
   successMessage: "Surgery removed successfully",
  });
 };

 saveSurgeryDetails = async () => {
  try {
   const { ok } = await this.ensureFreshBeforeAction();
   if (!ok) return;
   this.setState({ isSaving: true });

   // Payor panel, billed flags and status have dedicated flows; sending this
   // tab's copies would overwrite changes made by an Admin or another tab.
   const {
    postponementNotes,
    payorPanel,
    isBilled,
    markedBilledAt,
    markedBilledBy,
    status,
    ...restSxBooking
   } = this.state.sxBooking || {};

   const updatePayload = {
    ...restSxBooking,
    additionalNotes: this.state.sxBooking.notes,
    notes: this.state.sxBooking.notes,
    doctorId: this.state.sxBooking.surgeonId,
    doctorName: this.state.sxBooking.surgeon,
   };

   const response = await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${this.state.surgery.id}`,
    updatePayload
   );

   if (response.success) {
    cogoToast.success("Surgery details updated successfully");
    // Update original data to reflect saved changes
    this.setState((prevState) => ({
     originalSxBooking: JSON.parse(JSON.stringify(prevState.sxBooking)),
     isNotesDirty: false,
    }));
   } else {
    throw new Error(response.message || "Failed to update surgery details");
   }
  } catch (err) {
   cogoToast.error("Failed to update surgery details");
   console.error("Error saving surgery details:", err);
  } finally {
   this.setState({ isSaving: false });
  }
 };

 // Load printed names for Add Surgery (load all active, filter client-side by payor)
 loadPrintedNames = async () => {
  try {
   this.setState({ isLoadingPrintedNames: true });

   // Load all active surgeries - we'll filter by payor client-side
   const payload = { isActive: true };

   const response = await apiCallPost(
    `/v3/${this.props.authStore.prefix}/neoSxPaymentMaster/getAllPrintedNames`,
    payload
   );
   const printedNamesData = response?.data?.printedNames || [];

   console.log("✅ API returned", printedNamesData.length, "surgeries");

   // Preserve pricing and discounts so we can compute consolidatedBilling
   const printedNames = printedNamesData.map((item) => ({
    _id: item._id,
    printedName: item.printedName || item.surgeryName,
    payor: item.payor,
    group: item.group || "",
    // Flatten commonly used pricing fields for easier access
    pricing: {
     surgeryRate: Number(item?.pricing?.surgeryRate || 0),
     anaestheticCharges: Number(item?.pricing?.anaestheticCharges || 0),
     tissueProcessing: Number(item?.pricing?.tissueProcessing || 0),
     rate: Number(item?.pricing?.rate || 0),
     taxApplicable: Number(item?.pricing?.taxApplicable || 0),
     implantTax: Number(item?.pricing?.implantTax || 0),
     procedureCost: Number(item?.pricing?.procedureCost || 0),
     implantCost: Number(item?.pricing?.implantCost || 0),
     femto: Number(item?.pricing?.femto || 0),
     currency: item?.pricing?.currency || "INR",
     discountable: !!item?.pricing?.discountable,
     insuranceCoverage: {
      eligible: !!item?.pricing?.insuranceCoverage?.eligible,
      coveragePercentage: Number(
       item?.pricing?.insuranceCoverage?.coveragePercentage || 0
      ),
      maxCoverageAmount: Number(
       item?.pricing?.insuranceCoverage?.maxCoverageAmount || 0
      ),
     },
    },
    discounts: {
     specialDiscount: Number(item?.discounts?.specialDiscount || 0),
     mouDiscount: Number(item?.discounts?.mouDiscount || 0),
     iolDiscount: Number(item?.discounts?.iolDiscount || 0),
    },
   }));

   this.setState({ printedNames, isLoadingPrintedNames: false });
   console.log("✅ State updated with", printedNames.length, "surgeries");
  } catch (err) {
   console.error("Error loading printed names:", err);
   this.setState({ isLoadingPrintedNames: false });
  }
 };

 // Get filtered printed names based on payor panel in current booking
 getFilteredPrintedNames = () => {
  const panel = this.state.sxBooking?.payorPanel;
  const all = this.state.printedNames || [];

  console.log("🔍 getFilteredPrintedNames:", {
   totalPrintedNames: all.length,
   payorPanel: panel,
   uniquePayors: [...new Set(all.map((s) => s.payor))],
   sampleSurgeries: all
    .slice(0, 10)
    .map((s) => ({ name: s.printedName, payor: s.payor })),
  });

  if (!panel) return all;

  // Case-insensitive and trim comparison for better matching
  const filtered = all.filter((s) => {
   const surgeryPayor = (s.payor || "").trim().toUpperCase();
   const selectedPayor = (panel || "").trim().toUpperCase();
   return surgeryPayor === selectedPayor;
  });

  console.log(
   `✅ Filtered to ${filtered.length} surgeries for payor: ${panel}`
  );

  return filtered;
 };

 // Create surgery items for SingleSelectWrapper
 createSurgeryItems = () => {
  const filteredSurgeries = this.getFilteredPrintedNames();
  return filteredSurgeries
   .map(
    (surgery) =>
     `${surgery.printedName}${surgery.payor ? ` (${surgery.payor})` : ""}`
   )
   .filter(Boolean);
 };

 // Filter and sort surgery types by relevance (same as SurgeryBooking.js)
 filterAndSortSurgeryTypes = (query, surgeries) => {
  console.log("🔍 filterAndSortSurgeryTypes:", {
   query: query,
   inputCount: surgeries.length,
   sampleInput: surgeries.slice(0, 3).map((s) => s.printedName),
  });

  if (!query || !query.trim()) {
   // No query - return all surgeries sorted alphabetically
   const sorted = surgeries.sort((a, b) =>
    (a.printedName || "").localeCompare(b.printedName || "")
   );
   console.log("✅ No query, returning all", sorted.length, "surgeries sorted");
   return sorted;
  }

  const searchTerm = query.toLowerCase().trim();

  // Filter and score surgeries by relevance
  const scoredSurgeries = surgeries
   .filter((surgery) => {
    const surgeryName = (surgery.printedName || "").toLowerCase();
    return surgeryName.includes(searchTerm);
   })
   .map((surgery) => {
    const surgeryName = (surgery.printedName || "").toLowerCase();
    let score = 0;

    // Higher score for exact match
    if (surgeryName === searchTerm) {
     score = 1000;
    }
    // High score for starts with
    else if (surgeryName.startsWith(searchTerm)) {
     score = 500;
    }
    // Medium score for word boundary match
    else if (
     surgeryName.includes(" " + searchTerm) ||
     surgeryName.includes("-" + searchTerm)
    ) {
     score = 300;
    }
    // Lower score for contains
    else if (surgeryName.includes(searchTerm)) {
     score = 100;
    }

    // Boost score for shorter names (more specific matches)
    score += Math.max(0, 50 - surgeryName.length);

    return { ...surgery, searchScore: score };
   })
   .sort((a, b) => {
    // Sort by score (descending), then alphabetically
    if (b.searchScore !== a.searchScore) {
     return b.searchScore - a.searchScore;
    }
    return (a.printedName || "").localeCompare(b.printedName || "");
   });

  console.log(
   `✅ Filtered by "${searchTerm}":`,
   scoredSurgeries.length,
   "matches"
  );
  return scoredSurgeries;
 };

 // Highlight search text in surgery names (same as SurgeryBooking.js)
 highlightSearchText = (text, query) => {
  if (!query || !text) return text;

  const searchTerm = query.toLowerCase().trim();
  const textLower = text.toLowerCase();
  const index = textLower.indexOf(searchTerm);

  if (index === -1) return text;

  const beforeMatch = text.substring(0, index);
  const match = text.substring(index, index + searchTerm.length);
  const afterMatch = text.substring(index + searchTerm.length);

  return (
   <span>
    {beforeMatch}
    <strong
     style={{ backgroundColor: "rgba(19, 124, 189, 0.3)", padding: "2px 0" }}
    >
     {match}
    </strong>
    {afterMatch}
   </span>
  );
 };

 closeAddSurgeryDialog = () =>
  this.setState({
   showAddSurgeryDialog: false,
   selectedPrintedNameId: "",
   surgerySearchQuery: "",
  });

 confirmAddSurgery = async () => {
  try {
   const selectedId = this.state.selectedPrintedNameId;
   const options = this.getFilteredPrintedNames();
   const chosen = options.find((o) => o._id === selectedId);
   if (!chosen) {
    cogoToast.error("Please select a surgery to add");
    return;
   }

   const existing = this.state.sxBooking?.surgeries || [];
   const isDuplicate = existing.some((s) => (s.id || s._id) === chosen._id);
   if (isDuplicate) {
    cogoToast.info("This surgery is already added");
    return;
   }

   // Get default values from first surgery if available
   const firstSurgery = existing[0] || {};
   const defaultLaterality = firstSurgery.laterality || firstSurgery.eye || "";

   const newEntry = {
    id: chosen._id,
    name: chosen.printedName,
    payor: chosen.payor,
    printedName: chosen.printedName,
    group: chosen.group || "General",
    laterality: defaultLaterality,
    eye: defaultLaterality,
   };
   const newSurgeries = [...existing, newEntry];
   await this.updateSelectedSurgeriesOnServer(newSurgeries, {
    successMessage: "Surgery added successfully",
   });
   // Audit log for add
   try {
    const resourceId = this.state.surgery?.id;
    const resourceName = `Surgery - ${
     this.state.surgery?.patientName || "Unknown Patient"
    }`;
    if (resourceId) {
     await apiCallPost(
      `/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`,
      [
       {
        resourceType: "SURGERY_BOOKING",
        resourceId,
        resourceName,
        actionType: "CREATE",
        actionSlug: `Added surgery: "${newEntry.printedName || newEntry.name}"`,
        actionMetadata: {
         fieldName: "selectedSurgeries",
         changeType: "CREATE",
         previousValue: "",
         newValue: newEntry.printedName || newEntry.name || newEntry.id,
        },
       },
      ]
     );
    }
   } catch (e) {
    console.error("Audit log failed:", e);
   }
   this.closeAddSurgeryDialog();
  } catch (err) {
   console.error("Add surgery failed:", err);
  }
 };

 // Compute anesthesia rate from loaded options
 getAnesthesiaRate = () => {
  try {
   const selected = this.state.sxBooking?.anesthesia || "";
   if (!selected) return 0;
   const opt = (this.state.anesthesiaOptions || []).find(
    (o) => o.value === selected || o.label === selected
   );
   return Number(opt?.rate || 0);
  } catch (e) {
   return 0;
  }
 };

 // Ensure printed names are available (load if needed)
 ensurePrintedNamesLoaded = async () => {
  if ((this.state.printedNames || []).length > 0) return;
  // Attempt to load on-demand
  try {
   await this.loadPrintedNames();
  } catch (e) {
   // non-blocking
  }
 };

 // Compute consolidatedBilling snapshot from current surgeries + anesthesia
 resolveSpecialDiscountValue = (surgery, printedNamesMap = null) => {
  try {
   if (!surgery) {
    return 0;
   }
   const explicitFromDiscounts = surgery?.discounts;
   if (
    explicitFromDiscounts &&
    Object.prototype.hasOwnProperty.call(
     explicitFromDiscounts,
     "specialDiscount"
    )
   ) {
    const raw = Number(explicitFromDiscounts.specialDiscount);
    if (!Number.isNaN(raw)) {
     return raw;
    }
   }
   if (Object.prototype.hasOwnProperty.call(surgery, "specialDiscount")) {
    const raw = Number(surgery.specialDiscount);
    if (!Number.isNaN(raw)) {
     return raw;
    }
   }
   const pricingNode = surgery?.pricing;
   if (
    pricingNode &&
    Object.prototype.hasOwnProperty.call(pricingNode, "specialDiscount")
   ) {
    const raw = Number(pricingNode.specialDiscount);
    if (!Number.isNaN(raw)) {
     return raw;
    }
   }
   const id = surgery?.id || surgery?._id;
   if (id && printedNamesMap && printedNamesMap.has(id)) {
    const master = printedNamesMap.get(id);
    const masterDiscount = Number(
     master?.discounts?.specialDiscount || master?.specialDiscount || 0
    );
    if (!Number.isNaN(masterDiscount)) {
     return masterDiscount;
    }
   }
   return 0;
  } catch (error) {
   console.warn("Failed to resolve special discount:", error);
   return 0;
  }
 };

 calculateAutoSpecialDiscountPaise = (
  surgeries = [],
  printedNamesMap = null
 ) => {
  try {
   if (!Array.isArray(surgeries) || surgeries.length === 0) {
    return 0;
   }
   const map =
    printedNamesMap ||
    new Map((this.state.printedNames || []).map((p) => [p._id, p]));
   let totalRupees = 0;
   surgeries.forEach((surgery) => {
    totalRupees += this.resolveSpecialDiscountValue(surgery, map);
   });
   return Math.max(0, Math.round(totalRupees * 100));
  } catch (error) {
   console.warn("Auto special discount calc failed:", error);
   return 0;
  }
 };

 computeConsolidatedBillingFromSurgeries = async (
  surgeriesArray,
  options = {}
 ) => {
  const { prevSurgeries = null } = options || {};
  await this.ensurePrintedNamesLoaded();
  const printedNamesMap = new Map(
   (this.state.printedNames || []).map((p) => [p._id, p])
  );

  const surgeries = Array.isArray(surgeriesArray) ? surgeriesArray : [];
  let totalSurgeryRate = 0;
  let totalTissueProcessing = 0;
  let totalFemto = 0;
  let totalMouDiscount = 0;
  let totalSpecialDiscount = 0;

  surgeries.forEach((s) => {
   const id = s?.id || s?._id;
   const p = printedNamesMap.get(id);
   if (!p) return;
   const surgeryRate = Number(p?.pricing?.surgeryRate || 0);
   const tissueProcessing = Number(p?.pricing?.tissueProcessing || 0);
   const femto = Number(p?.pricing?.femto || 0);
   const mouPercent = Number(p?.discounts?.mouDiscount || 0);
   const specialDiscount = this.resolveSpecialDiscountValue(s, printedNamesMap);

   totalSurgeryRate += surgeryRate;
   totalTissueProcessing += tissueProcessing;
   totalFemto += femto;
   totalMouDiscount += Math.round((mouPercent / 100) * surgeryRate);
   totalSpecialDiscount += specialDiscount;
  });

  const anesthesiaRate = this.getAnesthesiaRate();
  const toPaise = (n) => Math.max(0, Math.round(Number(n || 0) * 100));

  const surgeryRatePaise = toPaise(totalSurgeryRate);
  const anaestheticChargesPaise = toPaise(anesthesiaRate);
  const tissueProcessingPaise = toPaise(totalTissueProcessing);
  const femtoChargesPaise = toPaise(totalFemto);
  const mouDiscountPaise = toPaise(totalMouDiscount);
  const autoSpecialDiscountPaise = toPaise(totalSpecialDiscount);

  const previousAutoSpecialPaise = Array.isArray(prevSurgeries)
   ? this.calculateAutoSpecialDiscountPaise(prevSurgeries, printedNamesMap)
   : null;

  const existing = this.state.surgery?.consolidatedBilling || {};
  const existingSpecialPaise = Number(existing?.specialDiscountPaise || 0);
  let specialDiscountPaise = autoSpecialDiscountPaise;

  const manualTolerancePaise = 5;
  const wasManualOverride =
   Number.isFinite(existingSpecialPaise) &&
   previousAutoSpecialPaise !== null &&
   Math.abs(existingSpecialPaise - previousAutoSpecialPaise) >
    manualTolerancePaise;

  if (wasManualOverride) {
   specialDiscountPaise = Math.max(0, Math.round(existingSpecialPaise));
  } else if (
   Array.isArray(prevSurgeries) &&
   previousAutoSpecialPaise !== null &&
   Number.isFinite(existingSpecialPaise)
  ) {
   const delta = autoSpecialDiscountPaise - previousAutoSpecialPaise;
   specialDiscountPaise = Math.max(0, Math.round(existingSpecialPaise + delta));
  }

  const combinedRatePaise = Math.max(
   0,
   surgeryRatePaise +
    anaestheticChargesPaise +
    tissueProcessingPaise +
    femtoChargesPaise -
    mouDiscountPaise -
    specialDiscountPaise
  );

  return {
   surgeryRatePaise,
   anaestheticChargesPaise,
   tissueProcessingPaise,
   combinedRatePaise,
   procedureTaxPercent: Number(existing?.procedureTaxPercent || 0),
   procedureCostRupees: Number(existing?.procedureCostRupees || 0),
   implantCostInclusivePaise: Number(existing?.implantCostInclusivePaise || 0),
   implantTaxPercent: Number(existing?.implantTaxPercent || 0),
   femtoChargesPaise,
   specialDiscountPaise,
   specialDiscountRemarks: existing?.specialDiscountRemarks || "",
   iolDiscountPaise: Number(existing?.iolDiscountPaise || 0),
   mouDiscountPercent: Number(existing?.mouDiscountPercent || 0),
   mouDiscountPaise,
   selectedAnesthesiaId:
    this.state.sxBooking?.anesthesia || existing?.selectedAnesthesiaId || "",
  };
 };

 // Helper to PATCH selectedSurgeries to server and update local state
 updateSelectedSurgeriesOnServer = async (
  surgeriesArray,
  { successMessage } = {}
 ) => {
  try {
   if (!this.state.surgery?.id) {
    cogoToast.error("Surgery booking not loaded");
    return;
   }
   const { ok } = await this.ensureFreshBeforeAction();
   if (!ok) return;
   const prevSurgeries = Array.isArray(this.state.sxBooking?.surgeries)
    ? [...this.state.sxBooking.surgeries]
    : [];
   const surgeryIdSet = (list) =>
    (list || [])
     .map((s) => String(s?.id || s?._id || ""))
     .sort()
     .join("|");
   const proceduresChanged = surgeryIdSet(prevSurgeries) !== surgeryIdSet(surgeriesArray);
   const payloadSelected = (surgeriesArray || []).map((s) => ({
    id: s.id || s._id,
    name: s.name || s.printedName,
    payor: s.payor || "",
    laterality: s.laterality || s.eye || "",
    group: s.group || "",
    estimatedCost: s.estimatedCost || 0,
   }));
   // Recompute consolidated billing snapshot from updated surgeries
   const consolidatedBilling =
    await this.computeConsolidatedBillingFromSurgeries(surgeriesArray, {
     prevSurgeries,
    });
   const anesthesia = this.state.sxBooking?.anesthesia || "";
   const procedureDischargeSummaryName =
    buildProcedureDischargeSummaryName(surgeriesArray);
   const response = await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${this.state.surgery.id}`,
    {
     selectedSurgeries: payloadSelected,
     consolidatedBilling,
     anesthesia,
     procedureDischargeSummaryName,
    }
   );
   if (response?.success) {
    await new Promise((resolveSetState) => {
     this.setState(
      (prev) => ({
       sxBooking: {
        ...prev.sxBooking,
        surgeries: surgeriesArray,
        selectedSurgeries: payloadSelected,
        procedureDischargeSummaryName,
       },
       originalSxBooking: {
        ...prev.originalSxBooking,
        surgeries: surgeriesArray,
        selectedSurgeries: payloadSelected,
        procedureDischargeSummaryName,
       },
       surgery: {
        ...(prev.surgery || {}),
        consolidatedBilling,
        anesthesia,
        selectedSurgeries: payloadSelected,
        procedureDischargeSummaryName,
       },
      }),
      resolveSetState
     );
    });
    // Keep cross-page cache in sync so BillingTab sees latest values immediately
    try {
     const surgeryId = this.state.surgery?.id;
     if (typeof window !== "undefined" && surgeryId) {
      window.__neoCache = window.__neoCache || {};
      window.__neoCache.bookingById = window.__neoCache.bookingById || {};
      const prevCache = window.__neoCache.bookingById[surgeryId] || {};
      window.__neoCache.bookingById[surgeryId] = {
       ...prevCache,
       // Persist latest selectedSurgeries and consolidated billing snapshot
       selectedSurgeries: payloadSelected,
       consolidatedBilling,
       anesthesia,
       // Signal BillingTab to drop any in-memory snapshot on next mount
       __billingInvalidateMemory: true,
      };
      // Clear inflight so next readers don't reuse stale promises
      if (window.__neoInflight?.bookingById?.[surgeryId]) {
       delete window.__neoInflight.bookingById[surgeryId];
      }
     }
    } catch (e) {
     // non-blocking cache sync
    }
    // Revalidate billed status only after state/cache sync
    if (proceduresChanged) {
     await this.unbillDueToSurgeryChange("Procedure added/removed");
    } else {
     await this.ensureUnbilledIfBalanceNonZero("surgery-update");
    }
    if (successMessage) cogoToast.success(successMessage);
   } else {
    throw new Error(response?.message || "Failed to update surgeries");
   }
  } catch (err) {
   cogoToast.error("Failed to update surgeries");
   console.error("Update selected surgeries failed:", err);
  }
 };

 // Expose updater for child component to change surgeries (e.g., laterality)
 onUpdateSurgeries = async (surgeriesArray) => {
  return this.updateSelectedSurgeriesOnServer(surgeriesArray, {});
 };

 // Fetch latest billing snapshot balance from server

 ensureUnbilledIfBalanceNonZero = async (
  reason = null,
  summaryOverride = null
 ) => {
  try {
   const billedFlag = Boolean(
    this.state.surgery?.isBilled || this.state.sxBooking?.isBilled
   );
   if (!billedFlag || this.autoUnbillInFlight) {
    if (!billedFlag) {
    }
    return;
   }
   this.autoUnbillInFlight = true;

   const isNonZero = (value) => Math.abs(Math.round(Number(value || 0))) > 0;

   const skipForStatePatch =
    reason === "state-patch" &&
    (!summaryOverride || typeof summaryOverride.balancePaise === "undefined");
   if (skipForStatePatch) {
    return;
   }

   const normalizeOverride = () => {
    if (!summaryOverride || typeof summaryOverride !== "object") {
     return null;
    }
    const normalized = Number(
     summaryOverride.balancePaise ??
      summaryOverride.balance ??
      summaryOverride.balanceInPaise ??
      summaryOverride.balancePaiseOverride ??
      0
    );
    if (Number.isNaN(normalized)) {
     return null;
    }
    return {
     balancePaise: normalized,
     source: summaryOverride.source || reason || "billing-tab",
    };
   };

   // When the Billing tab supplies its balance, that figure is authoritative.
   // Falling back to other snapshots whenever it reads zero let any rounding
   // difference between them unbill a fully settled booking.
   let summary = normalizeOverride();
   if (!summary) {
    summary = this.computeLocalBalanceSnapshot() || this.computeCachedBalanceSnapshot();
   }
   const balancePaise = summary ? Number(summary.balancePaise || 0) : 0;

   if (!summary) {
    return;
   }

   if (!isNonZero(balancePaise)) {
    return;
   }

   await this.forceUnbillDueToBalance(
    balancePaise,
    summary.source || reason || "local"
   );
  } catch (err) {
   console.warn("Auto-unbill check failed:", err);
  } finally {
   this.autoUnbillInFlight = false;
  }
 };

 getAuthorizedAmountPaise = (surgeryData) => {
  try {
   const panel = String(surgeryData?.payorPanel || this.getCurrentPayorPanel() || "")
    .trim()
    .toLowerCase();
   if (panel === "cash") return 0;
   const auth = surgeryData?.authorization;
   if (!auth) return 0;
   const entries = Array.isArray(auth.entries) ? auth.entries : [];
   if (entries.length === 0) return 0;
   let entry = null;
   if (
    typeof auth.activeIndex === "number" &&
    auth.activeIndex >= 0 &&
    auth.activeIndex < entries.length
   ) {
    entry = entries[auth.activeIndex];
   } else {
    entry = entries[entries.length - 1];
   }
   if (!entry) return 0;
   if (typeof entry.amountPaise === "number") {
    return Math.max(0, Math.round(entry.amountPaise));
   }
   if (typeof entry.amountRupees === "number") {
    return Math.max(0, Math.round(entry.amountRupees * 100));
   }
   const parsed = Number(entry.amountPaise || entry.amountRupees || 0);
   if (!Number.isNaN(parsed)) {
    return Math.max(0, Math.round(parsed * (entry.amountRupees ? 100 : 1)));
   }
   return 0;
  } catch (err) {
   console.warn("Authorized amount extraction failed:", err);
   return 0;
  }
 };

 computeLocalBalanceSnapshot = (surgeryOverride = null) => {
  try {
   const surgeryData = surgeryOverride || this.state.surgery;
   if (!surgeryData) return null;
   const consolidated = surgeryData.consolidatedBilling;
   if (!consolidated) return null;
   const toNumber = (value) => {
    if (typeof value === "number" && !Number.isNaN(value)) return value;
    const parsed = Number(value || 0);
    return Number.isNaN(parsed) ? 0 : parsed;
   };

   const discountPaise =
    toNumber(consolidated.specialDiscountPaise) +
    toNumber(consolidated.mouDiscountPaise) +
    toNumber(consolidated.iolDiscountPaise);
   const grossPaise =
    toNumber(consolidated.surgeryRatePaise) +
    toNumber(consolidated.anaestheticChargesPaise) +
    toNumber(consolidated.tissueProcessingPaise) +
    toNumber(consolidated.femtoChargesPaise) +
    toNumber(consolidated.procedureCostPaise) +
    toNumber(consolidated.implantCostInclusivePaise);
   const netFromComponentsPaise = grossPaise - discountPaise;

   const recordedNetPaise = toNumber(consolidated.combinedRatePaise);
   const tolerancePaise = 5; // allow minor rounding differences
   let netReceivablePaise = netFromComponentsPaise;
   if (recordedNetPaise > 0) {
    netReceivablePaise =
     Math.abs(recordedNetPaise - netFromComponentsPaise) <= tolerancePaise
      ? netFromComponentsPaise
      : recordedNetPaise;
   }

   const positiveReceivablePaise = Math.max(netReceivablePaise, 0);
   if (positiveReceivablePaise === 0 && netReceivablePaise === 0) {
    return null;
   }

   const authorizedPaiseRaw = this.getAuthorizedAmountPaise(surgeryData);
   const authorizedPaise = Math.min(
    positiveReceivablePaise,
    Math.max(0, Math.round(Number(authorizedPaiseRaw) || 0))
   );
   const receivableAfterAuthPaise = netReceivablePaise - authorizedPaise;

   const paymentSource = surgeryData.payments;
   let paymentEvents = [];
   let collectedPaise = 0;
   let refundedPaise = 0;

   if (Array.isArray(paymentSource)) {
    paymentEvents = paymentSource;
   } else if (paymentSource && typeof paymentSource === "object") {
    if (Array.isArray(paymentSource.events)) {
     paymentEvents = paymentSource.events;
    }
    collectedPaise = toNumber(
     paymentSource.totalPaidPaise ??
      (paymentSource.totalPaid ? Number(paymentSource.totalPaid) * 100 : 0)
    );
    refundedPaise = toNumber(
     paymentSource.totalRefundedPaise ??
      (paymentSource.totalRefunded
       ? Number(paymentSource.totalRefunded) * 100
       : 0)
    );
   }

   if (paymentEvents.length > 0) {
    collectedPaise = 0;
    refundedPaise = 0;
    paymentEvents.forEach((evt) => {
     const amountPaise =
      typeof evt?.amountPaise === "number"
       ? evt.amountPaise
       : Math.round(Number(evt?.amount || 0) * 100);
     if (!amountPaise || Number.isNaN(amountPaise)) return;
     if (String(evt?.type || "").toLowerCase() === "refund") {
      refundedPaise += amountPaise;
     } else {
      collectedPaise += amountPaise;
     }
    });
   }

   const balancePaise =
    receivableAfterAuthPaise - collectedPaise + refundedPaise;
   return {
    balancePaise,
    totalReceivablePaise: netReceivablePaise,
    authorizedPaise,
    receivableAfterAuthPaise,
    collectedPaise,
    refundedPaise,
    source: surgeryOverride ? "cache" : "local",
   };
  } catch (err) {
   console.warn("Failed to compute local balance snapshot:", err);
   return null;
  }
 };

 computeCachedBalanceSnapshot = () => {
  try {
   const surgeryId = this.state.surgery?.id;
   if (!surgeryId || typeof window === "undefined") return null;
   const cached = window.__neoCache?.bookingById?.[surgeryId];
   if (!cached) return null;
   return this.computeLocalBalanceSnapshot(cached);
  } catch (err) {
   console.warn("Failed to compute cached balance snapshot:", err);
   return null;
  }
 };

 forceUnbillDueToBalance = async (balancePaise, source = "snapshot") => {
  try {
   const surgeryId = this.state.surgery?.id;
   if (!surgeryId) return;
   const res = await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${surgeryId}`,
    { isBilled: false }
   );
   if (res?.success) {
    this.handleStatusChangeFromBilling({ isBilled: false });
    await this.recordAutoUnbillAudit(balancePaise, source);
    cogoToast.info(
     "Balance changed after surgery update. Marked as not billed."
    );
   }
  } catch (err) {
   console.error("Failed to auto-unbill after surgery change:", err);
  }
 };

 // Anaesthesia changes and procedure add/remove alter the bill, so a billed
 // booking goes back to Unbilled (which also unlocks the Billing section).
 unbillDueToSurgeryChange = async (changeLabel) => {
  try {
   const surgeryId = this.state.surgery?.id;
   const billed = Boolean(this.state.surgery?.isBilled || this.state.sxBooking?.isBilled);
   if (!surgeryId || !billed) return;
   const res = await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${surgeryId}`,
    { isBilled: false }
   );
   if (!res?.success) return;
   this.handleStatusChangeFromBilling({ isBilled: false });
   await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`, [
    {
     resourceType: "SURGERY_BOOKING",
     resourceId: surgeryId,
     resourceName: `Surgery - ${this.state.surgery?.patientName || "Unknown Patient"}`,
     actionType: "UPDATE",
     actionSlug: `Auto unbilled: ${changeLabel}`,
     actionMetadata: {
      fieldName: "isBilled",
      changeType: "UPDATE",
      previousValue: true,
      newValue: false,
      reason: changeLabel,
     },
    },
   ]);
   cogoToast.info(`${changeLabel}. Marked as not billed — please review Billing.`, {
    hideAfter: 6,
   });
  } catch (err) {
   console.error("Failed to unbill after surgery change:", err);
  }
 };

 // Re-reads the booking before a workflow write. If an Admin or another tab
 // changed status, billed flag or payor panel since this page loaded, the
 // action is blocked and the page reloads so the user acts on current data.
 ensureFreshBeforeAction = async () => {
  try {
   const surgeryId = this.state.surgery?.id;
   const prefix = this.props.authStore?.prefix;
   if (!surgeryId || !prefix) return { ok: true, fresh: null };
   const res = await apiCallGet(
    `/v3/${prefix}/neoSxBookingMaster/getById/${surgeryId}?_ts=${Date.now()}`
   );
   const fresh = res?.success ? res.data : null;
   if (!fresh) return { ok: true, fresh: null };

   const norm = (v) => String(v || "").trim().toLowerCase();
   const freshPanel = fresh.payorPanel || (fresh.selectedSurgeries || [])[0]?.payor || "";
   const changes = [];
   if (norm(fresh.status) !== norm(this.state.surgery?.status || this.state.sxBooking?.status)) {
    changes.push(`status is now "${fresh.status || "-"}"`);
   }
   const localBilled = Boolean(this.state.surgery?.isBilled || this.state.sxBooking?.isBilled);
   if (Boolean(fresh.isBilled) !== localBilled) {
    changes.push(fresh.isBilled ? "it was marked billed" : "it was marked not billed");
   }
   if (norm(freshPanel) !== norm(this.getCurrentPayorPanel())) {
    changes.push(`payor panel is now "${freshPanel || "-"}"`);
   }
   if (changes.length === 0) return { ok: true, fresh };

   this.reloadForServerChange(changes);
   return { ok: false, fresh };
  } catch (err) {
   console.error("Freshness check failed:", err);
   return { ok: true, fresh: null };
  }
 };

 reloadForServerChange = (changes = [], { message } = {}) => {
  cogoToast.warn(
   message ||
    `This surgery was updated elsewhere (${changes.join(", ")}). Reloading with the latest data — please redo your action.`,
   { hideAfter: 6 }
  );
  try {
   const surgeryId = this.state.surgery?.id;
   if (surgeryId && window.__neoCache?.bookingById) {
    delete window.__neoCache.bookingById[surgeryId];
   }
  } catch (e) {}
  setTimeout(() => window.location.reload(), 1500);
 };

 recordAutoUnbillAudit = async (balancePaise, source = "snapshot") => {
  try {
   const surgeryId = this.state.surgery?.id;
   if (!surgeryId) return;
   const resourceName = `Surgery - ${
    this.state.surgery?.patientName || "Unknown Patient"
   }`;
   const entry = {
    resourceType: "SURGERY_BOOKING",
    resourceId: surgeryId,
    resourceName,
    actionType: "UPDATE",
    actionSlug: "Auto unbilled due to balance change",
    actionMetadata: {
     fieldName: "isBilled",
     changeType: "UPDATE",
     previousValue: true,
     newValue: false,
     reason: "balance_non_zero_after_surgery_change",
     balancePaise: Number(balancePaise || 0),
     balanceSource: source,
    },
   };
   await apiCallPost(
    `/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`,
    [entry]
   );
  } catch (err) {
   console.error("Auto-unbill audit failed:", err);
  }
 };

recordPostponeAudit = async (
 previousStatus,
 surgeryIdParam = null,
 postponementNotes = ""
) => {
  try {
   const surgeryId = surgeryIdParam || this.state.surgery?.id;
   if (!surgeryId) return;
   const resourceName = `Surgery - ${
    this.state.surgery?.patientName || "Unknown Patient"
   }`;
   await apiCallPost(
    `/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`,
    [
     {
      resourceType: "SURGERY_BOOKING",
      resourceId: surgeryId,
      resourceName,
      actionType: "UPDATE",
      actionSlug: `Updated status: "${
       previousStatus || "Empty"
      }" → "Postponed"`,
      actionMetadata: {
       fieldName: "status",
       changeType: "UPDATE",
       previousValue: previousStatus || "",
       newValue: "Postponed",
       postponementNotes: postponementNotes ? postponementNotes : undefined,
      },
     },
    ]
   );
  } catch (err) {
   console.error("Postpone audit failed:", err);
  }
 };

 // Save Medical Team section
 saveMedicalTeam = async () => {
  if (!this.state.sxBooking.advisedById && !this.state.sxBooking.advisedBy) {
   cogoToast.error("Please select an advising doctor");
   return;
  }

  try {
   const { ok } = await this.ensureFreshBeforeAction();
   if (!ok) return;
   this.setState({ isSavingMedicalTeam: true });

   const medicalTeamPayload = {
    doctorId: this.state.sxBooking.surgeonId,
    doctorName: this.state.sxBooking.surgeon,
    secondarySurgeonId: this.state.sxBooking.secondarySurgeonId,
    secondarySurgeon: this.state.sxBooking.secondarySurgeon,
    anaesthetistId: this.state.sxBooking.anaesthetistId,
    anaesthetist: this.state.sxBooking.anaesthetist,
    advisedById: this.state.sxBooking.advisedById,
    advisedBy: this.state.sxBooking.advisedBy,
    chosenBy: this.state.sxBooking.chosenBy,
   };

   const response = await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${this.state.surgery.id}`,
    medicalTeamPayload
   );

   if (response.success) {
    cogoToast.success("Medical team updated successfully");
    // Update original data to reflect saved changes
    this.setState((prevState) => ({
     originalSxBooking: JSON.parse(JSON.stringify(prevState.sxBooking)),
     isMedicalTeamDirty: false,
    }));
   } else {
    throw new Error(response.message || "Failed to update medical team");
   }
  } catch (err) {
   cogoToast.error("Failed to update medical team");
   console.error("Error saving medical team:", err);
  } finally {
   this.setState({ isSavingMedicalTeam: false });
  }
 };

 // Save Schedule section
 saveSchedule = async (extraPayload = {}) => {
  try {
   const { ok } = await this.ensureFreshBeforeAction();
   if (!ok) return false;
   this.setState({ isSavingSchedule: true });

   const formattedTime = this.formatScheduleTime(this.state.sxBooking.time);
   const resolvedTimeFrame = this.buildScheduleTimeFrame(
    this.state.sxBooking.time,
    this.state.sxBooking.timeFrame,
    this.state.sxBooking.estimatedDuration
   );

   const schedulePayload = {
    scheduledDate: this.state.sxBooking.date,
    scheduledTime: formattedTime,
    date: this.state.sxBooking.date,
    time: formattedTime,
    otTheatre: this.state.sxBooking.otTheatre,
    room: this.state.sxBooking.room,
    ...extraPayload,
   };

   if (resolvedTimeFrame) {
    schedulePayload.timeFrame = resolvedTimeFrame;
   }
   if (
    this.state.sxBooking.slotCapacity !== undefined &&
    this.state.sxBooking.slotCapacity !== null
   ) {
    schedulePayload.slotCapacity = this.state.sxBooking.slotCapacity;
   }
   if (
    this.state.sxBooking.slotBookedCount !== undefined &&
    this.state.sxBooking.slotBookedCount !== null
   ) {
    schedulePayload.slotBookedCount = this.state.sxBooking.slotBookedCount;
   }

   // Include rescheduled flag if it exists in sxBooking state
   if (this.state.sxBooking.rescheduled !== undefined) {
    schedulePayload.rescheduled = this.state.sxBooking.rescheduled;
   }

   const response = await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${this.state.surgery.id}`,
    schedulePayload
   );

   if (response.success) {
    cogoToast.success("Schedule updated successfully");
    // Update original data to reflect saved changes
    this.setState((prevState) => ({
     sxBooking: {
      ...prevState.sxBooking,
      timeFrame: resolvedTimeFrame || prevState.sxBooking.timeFrame,
     },
     originalSxBooking: JSON.parse(
      JSON.stringify({
       ...prevState.sxBooking,
       timeFrame: resolvedTimeFrame || prevState.sxBooking.timeFrame,
      })
     ),
     isScheduleDirty: false,
    }));
    return true;
   } else {
    throw new Error(response.message || "Failed to update schedule");
   }
  } catch (err) {
   cogoToast.error(err?.message || "Failed to update schedule");
   console.error("Error saving schedule:", err);
   return false;
  } finally {
   this.setState({ isSavingSchedule: false });
  }
 };

postponeSurgery = async (payload = {}) => {
  try {
   const maxLength = 2000;
   const trimmedNotes = (payload?.postponementNotes || "").trim();

   if (!trimmedNotes) {
    cogoToast.error("Postponement note is required");
    return;
   }

   if (trimmedNotes.length > maxLength) {
    cogoToast.error(`Postponement note must be under ${maxLength} characters`);
    return;
   }

   if (!this.state.surgery?.id) {
    cogoToast.error("Surgery not loaded");
    return;
   }

   if (!this.isValidObjectId(this.state.surgery.id)) {
    cogoToast.error("Invalid surgery ID");
    return;
   }

   const { ok, fresh } = await this.ensureFreshBeforeAction();
   if (!ok) return;
   const postponeBlock = this.getPostponeDisableReason(fresh || this.state.surgery);
   if (postponeBlock) {
    cogoToast.error(postponeBlock, { hideAfter: 6 });
    return;
   }

   this.setState({ isPostponingSurgery: true });

   const surgeryId = this.state.surgery.id;
   const previousStatus =
    this.state.surgery?.status || this.state.sxBooking?.status || "";

   const response = await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${surgeryId}`,
    {
     scheduledDate: null,
     scheduledTime: null,
     date: null,
     time: null,
     status: "Postponed",
     postponementNotes: trimmedNotes,
    }
   );

   if (response?.success) {
    this.setState(
     (prevState) => {
      const updatedSxBooking = {
       ...prevState.sxBooking,
       date: null,
       time: null,
       status: "Postponed",
       postponementNotes: trimmedNotes,
      };
      return {
       sxBooking: updatedSxBooking,
       originalSxBooking: JSON.parse(JSON.stringify(updatedSxBooking)),
       surgery: {
        ...prevState.surgery,
        scheduledDate: null,
        scheduledTime: null,
        status: "Postponed",
        postponementNotes: trimmedNotes,
       },
       isScheduleDirty: false,
      };
     },
     () => {
      try {
       if (
        typeof window !== "undefined" &&
        window.__neoCache?.bookingById?.[surgeryId]
       ) {
        window.__neoCache.bookingById[surgeryId] = {
         ...window.__neoCache.bookingById[surgeryId],
         scheduledDate: null,
         scheduledTime: null,
         status: "Postponed",
         postponementNotes: trimmedNotes,
        };
       }
      } catch (e) {}
     }
    );

    cogoToast.success("Surgery postponed");
    await this.recordPostponeAudit(previousStatus, surgeryId, trimmedNotes);
   } else {
    throw new Error(response?.message || "Failed to postpone surgery");
   }
  } catch (err) {
   cogoToast.error("Failed to postpone surgery");
   console.error("Error postponing surgery:", err);
  } finally {
   this.setState({ isPostponingSurgery: false });
  }
 };

 // Save Clinical Details section
 saveClinicalDetails = async () => {
  try {
   const { ok } = await this.ensureFreshBeforeAction();
   if (!ok) return;
   this.setState({ isSavingClinicalDetails: true });
   const anesthesiaChanged =
    String(this.state.originalSxBooking?.anesthesia || "") !==
    String(this.state.sxBooking?.anesthesia || "");

   const currentSurgeries = Array.isArray(this.state.sxBooking?.surgeries)
    ? [...this.state.sxBooking.surgeries]
    : [];
   const consolidatedBilling =
    currentSurgeries.length > 0
     ? await this.computeConsolidatedBillingFromSurgeries(currentSurgeries, {
        prevSurgeries: currentSurgeries,
       })
     : null;

   const clinicalPayload = {
    // anesthesia moved to overall clinical level
    anesthesia: this.state.sxBooking.anesthesia,
    dietPreference: this.state.sxBooking.diet,
    diet: this.state.sxBooking.diet,
    isDiabetic: this.state.sxBooking.diabetic,
    diabetic: this.state.sxBooking.diabetic,
    additionalNotes: this.state.sxBooking.notes,
    notes: this.state.sxBooking.notes,
    // include IOLitems to keep server in sync even if only IOLs changed
    IOLitems: Array.isArray(this.state.sxBooking.IOLitems)
     ? this.state.sxBooking.IOLitems
     : [],
    ...(consolidatedBilling ? { consolidatedBilling } : {}),
   };

   const response = await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${this.state.surgery.id}`,
    clinicalPayload
   );

   if (response.success) {
    cogoToast.success("Clinical details updated successfully");
    // Update original data to reflect saved changes
    this.setState(
     (prevState) => ({
      originalSxBooking: JSON.parse(JSON.stringify(prevState.sxBooking)),
      surgery: {
       ...prevState.surgery,
       anesthesia: prevState.sxBooking.anesthesia,
       ...(consolidatedBilling ? { consolidatedBilling } : {}),
      },
      isClinicalDetailsDirty: false,
     }),
     () => {
      try {
       const surgeryId = this.state.surgery?.id;
       if (surgeryId && window.__neoCache?.bookingById?.[surgeryId]) {
        window.__neoCache.bookingById[surgeryId] = {
         ...window.__neoCache.bookingById[surgeryId],
         anesthesia: this.state.sxBooking.anesthesia,
         ...(consolidatedBilling ? { consolidatedBilling } : {}),
         __billingInvalidateMemory: true,
        };
       }
      } catch (e) {
       // Non-blocking cache update
      }
     }
    );
    if (anesthesiaChanged) {
     await this.unbillDueToSurgeryChange("Anaesthesia changed");
    }
   } else {
    throw new Error(response.message || "Failed to update clinical details");
   }
  } catch (err) {
   cogoToast.error("Failed to update clinical details");
   console.error("Error saving clinical details:", err);
  } finally {
   this.setState({ isSavingClinicalDetails: false });
  }
 };

 // Inline edit: toggle and save patient details (Name, DOB/Sex, Address)
 toggleEditDetails = async () => {
  try {
   if (this.state.isEditingDetails) {
    await this.savePatientDetails();
   } else {
    const name = this.state.surgery?.patientName || "";
    const phone = this.state.surgery?.patientPhone || "";
    const address =
     this.state.surgery?.patientAddress ||
     this.state.surgery?.patientOriginalData?.address ||
     "";
    const sexRaw =
     this.state.surgery?.patientSex || this.state.sxBooking?.patientSex || "";
    const sexLower = (sexRaw || "").toString().toLowerCase();
    const sex =
     sexLower === "m" || sexLower === "male"
      ? "Male"
      : sexLower === "f" || sexLower === "female"
      ? "Female"
      : sexRaw || "";
    const dobStr =
     this.state.surgery?.patientDob ||
     this.state.surgery?.patientOriginalData?.dob ||
     null;
    const rawDob = dobStr ? new Date(dobStr) : null;
    const minDob = new Date(1900, 0, 1);
    const maxDob = new Date();
    const dob = this.clampDate(rawDob, minDob, maxDob);
    this.setState({
     editPatientName: name,
     editPatientPhone: phone,
     editAddress: address,
     editSex: sex,
     editDob: dob,
    });
   }
  } finally {
   this.setState(
    (prev) => ({ isEditingDetails: !prev.isEditingDetails }),
    () => setTimeout(this.updateHeaderRowHeight, 0)
   );
  }
 };

 savePatientDetails = async () => {
  try {
   if (!this.state.surgery?.id) return;
   this.setState({ isSavingPatientDetails: true });
   cogoToast.loading("Saving patient details...");

   const dobIso =
    this.state.editDob instanceof Date && !isNaN(this.state.editDob)
     ? new Date(
        this.state.editDob.getTime() -
         this.state.editDob.getTimezoneOffset() * 60000
       ).toISOString()
     : null;

   const age = dobIso
    ? this.calculateAgeFromDob(dobIso)
    : this.state.surgery?.patientAge || null;
   const sexVal = (() => {
    const s = (this.state.editSex || "").toString().toLowerCase();
    if (s === "m" || s === "male") return "Male";
    if (s === "f" || s === "female") return "Female";
    return this.state.editSex || "";
   })();

   const payload = {
    patientName: this.state.editPatientName || "",
    patientPhone: this.state.editPatientPhone || "",
    patientAddress: this.state.editAddress || "",
    patientSex: sexVal,
    ...(dobIso ? { patientDob: dobIso, patientAge: age } : {}),
   };

   const response = await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${this.state.surgery.id}`,
    payload
   );

   if (response?.success) {
    cogoToast.success("Patient details updated");
    // Prepare audit logs for changed fields (with before -> after in description)
    try {
     const normSex = (val) => {
      const s = (val || "").toString().trim().toLowerCase();
      if (s === "m" || s === "male") return "Male";
      if (s === "f" || s === "female") return "Female";
      return val || "";
     };
     const normDate = (val) => {
      if (!val) return "";
      const d = val instanceof Date ? val : new Date(val);
      if (isNaN(d)) return "";
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
     };

     const before = {
      patientName: this.state.surgery?.patientName || "",
      patientPhone: this.state.surgery?.patientPhone || "",
      patientAddress:
       this.state.surgery?.patientAddress ||
       this.state.surgery?.patientOriginalData?.address ||
       "",
      patientSex: this.state.surgery?.patientSex || "",
      patientDob:
       this.state.surgery?.patientDob ||
       this.state.surgery?.patientOriginalData?.dob ||
       "",
     };
     const after = {
      patientName: this.state.editPatientName || "",
      patientPhone: this.state.editPatientPhone || "",
      patientAddress: this.state.editAddress || "",
      patientSex: sexVal,
      patientDob: dobIso || before.patientDob,
     };

     const beforeDisplay = {
      patientName: before.patientName,
      patientPhone: before.patientPhone,
      patientAddress: before.patientAddress,
      patientSex: normSex(before.patientSex),
      patientDob: normDate(before.patientDob),
     };
     const afterDisplay = {
      patientName: after.patientName,
      patientPhone: after.patientPhone,
      patientAddress: after.patientAddress,
      patientSex: normSex(after.patientSex),
      patientDob: normDate(after.patientDob),
     };

     const changes = [
      { key: "patientName", label: "Patient Name" },
      { key: "patientPhone", label: "Patient Phone" },
      { key: "patientAddress", label: "Patient Address" },
      { key: "patientSex", label: "Patient Sex" },
      { key: "patientDob", label: "Patient DOB" },
     ]
      .filter(
       ({ key }) => (beforeDisplay[key] || "") !== (afterDisplay[key] || "")
      )
      .map(({ key, label }) => ({
       resourceType: "SURGERY_BOOKING",
       resourceId: this.state.surgery?.id,
       resourceName: `Surgery - ${
        this.state.surgery?.patientName || "Unknown Patient"
       }`,
       actionType: "UPDATE",
       actionSlug: `Edited ${label}: ${beforeDisplay[key] || "Empty"} → ${
        afterDisplay[key] || "Empty"
       }`,
       actionMetadata: {
        fieldName: key,
        changeType: "UPDATE",
        previousValue: beforeDisplay[key] || "",
        newValue: afterDisplay[key] || "",
       },
      }));
     if (changes.length > 0) {
      await apiCallPost(
       `/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`,
       changes
      );
     }
    } catch (auditErr) {
     console.error("Audit log failed:", auditErr);
    }
    this.setState(
     (prev) => ({
      surgery: {
       ...prev.surgery,
       patientName: this.state.editPatientName || "",
       patientPhone: this.state.editPatientPhone || "",
       patientAddress: this.state.editAddress || "",
       patientSex: sexVal,
       patientAge: dobIso ? age : prev.surgery?.patientAge,
       patientDob: dobIso || prev.surgery?.patientDob,
       patientOriginalData: {
        ...(prev.surgery?.patientOriginalData || {}),
        dob: dobIso || prev.surgery?.patientOriginalData?.dob,
        address:
         this.state.editAddress || prev.surgery?.patientOriginalData?.address,
       },
      },
      sxBooking: {
       ...prev.sxBooking,
       patientName: this.state.editPatientName || prev.sxBooking?.patientName,
       patientPhone:
        this.state.editPatientPhone || prev.sxBooking?.patientPhone,
       patientSex: sexVal,
       patientAge: dobIso ? age : prev.sxBooking?.patientAge,
      },
     }),
     () => {
      try {
       const sid = this.state.surgery?.id;
       if (sid && window.__neoCache?.bookingById?.[sid]) {
        window.__neoCache.bookingById[sid] = {
         ...window.__neoCache.bookingById[sid],
         patientName: this.state.editPatientName || "",
         patientPhone: this.state.editPatientPhone || "",
         patientAddress: this.state.editAddress || "",
         patientSex: sexVal,
         ...(dobIso ? { patientDob: dobIso, patientAge: age } : {}),
        };
       }
      } catch (e) {}
     }
    );
   } else {
    throw new Error(response?.message || "Failed to update patient details");
   }
  } catch (err) {
   cogoToast.error("Failed to update patient details");
   console.error("Error updating patient details:", err);
  } finally {
   this.setState({ isSavingPatientDetails: false });
  }
 };

 // Handle anesthesia change from BillingTab
 handleAnesthesiaChangeFromBilling = (anesthesiaSelection) => {
  try {
   if (this.isDischargedStatus() || this.isCancelledStatus()) {
    const statusLabel = this.isCancelledStatus() ? "cancelled" : "discharged";
    cogoToast.warn(`Anesthesia cannot be modified after surgery is ${statusLabel}`);
    return;
   }
  } catch (e) {
   // If status check fails for any reason, fall back to allowing update
  }

  console.log(
   "🩺 SurgeryBookingDetails: Anesthesia changed from billing:",
   anesthesiaSelection
  );

  // Update the sxBooking state
  this.setState(
   (prevState) => ({
    sxBooking: {
     ...prevState.sxBooking,
     anesthesia: anesthesiaSelection,
    },
    surgery: {
     ...prevState.surgery,
     anesthesia: anesthesiaSelection,
    },
    isClinicalDetailsDirty: true,
   }),
   () => {
    // Auto-save the clinical details with the new anesthesia selection
    this.saveClinicalDetails();
   }
  );
 };

 // Merge-update IOL items in state and cache when child saves/removes
 handleIOLItemsChanged = async (iolItems) => {
  try {
   const surgeryId = this.state.surgery?.id;
   this.setState(
    (prev) => ({
     sxBooking: {
      ...prev.sxBooking,
      IOLitems: Array.isArray(iolItems) ? iolItems : [],
     },
     surgery: {
      ...prev.surgery,
      IOLitems: Array.isArray(iolItems) ? iolItems : [],
     },
    }),
    () => {
     try {
      if (surgeryId && window.__neoCache?.bookingById?.[surgeryId]) {
       window.__neoCache.bookingById[surgeryId] = {
        ...window.__neoCache.bookingById[surgeryId],
        IOLitems: Array.isArray(iolItems) ? iolItems : [],
       };
      }
     } catch (e) {
      // non-blocking cache update
     }
    }
   );
  } catch (e) {
   // swallow
  }
 };

 // Handle payment updates from BillingTab (for real-time cancellation validation)
 handlePaymentsUpdate = (updatedPayments) => {
  console.log(
   "💰 SurgeryBookingDetails: Payments updated from billing:",
   updatedPayments
  );

  this.setState(
   (prevState) => ({
    surgery: {
     ...(prevState.surgery || {}),
     payments: updatedPayments || [],
    },
   }),
   () => {}
  );

  // Also update cache to keep it in sync
  try {
   const surgeryId = this.state.surgery?.id;
   if (surgeryId && window.__neoCache?.bookingById?.[surgeryId]) {
    window.__neoCache.bookingById[surgeryId] = {
     ...window.__neoCache.bookingById[surgeryId],
     payments: updatedPayments || [],
    };
   }
  } catch (e) {
   // Non-blocking cache update
   console.warn("Failed to update cache with new payments:", e);
  }
 };

 // Update discharge data in parent state after child saves
 updateDischargeData = async (dischargeData = {}) => {
  try {
   const keysToSync = [
    "dischargeDiagnosis",
    "reasonForAdmission",
    "investigation",
    "significantFindings",
    "dischargeOtNotes",
    "selectedTemplateId",
    "selectedTemplateName",
    "selectedTemplateContent",
    "procedureDischargeSummaryName",
   ];

   const updates = {};
   keysToSync.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(dischargeData, key)) {
     updates[key] = dischargeData[key] ?? "";
    }
   });

   if (Object.keys(updates).length === 0) {
    return;
   }

   this.setState((prevState) => ({
    surgery: {
     ...prevState.surgery,
     ...updates,
    },
   }));

   // Also update the cache to ensure consistency
   const surgeryId = this.state.surgery?.id;
   if (surgeryId && window.__neoCache?.bookingById?.[surgeryId]) {
    window.__neoCache.bookingById[surgeryId] = {
     ...window.__neoCache.bookingById[surgeryId],
     ...updates,
    };
   }
  } catch (err) {
   console.error("Error updating discharge data in parent:", err);
  }
 };

refreshCancelEligibilityForLoadedSurgery = async (surgeryId) => {
    try {
        if (!surgeryId || !this.isValidObjectId(surgeryId)) {
            return;
        }
        await this.refreshSurgeryPaymentsFromServerForCancelCheck(surgeryId, {
            silent: true,
        });
    } finally {
        try {
            if (this.state?.surgery?.id === surgeryId) {
                this.setState({ isCheckingCancelEligibility: false });
            }
        } catch (e) {}
    }
};

_cancelEligibilityInflight = null;

refreshSurgeryPaymentsFromServerForCancelCheck = async (surgeryId, opts = {}) => {
    const { silent = false } = opts || {};
    try {
        const prefix = this.props.authStore?.prefix;
        if (!prefix || !surgeryId || !this.isValidObjectId(surgeryId)) {
            return null;
        }

        if (!this._cancelEligibilityInflight) {
            const url = `/v3/${prefix}/neoSxBookingMaster/getById/${surgeryId}?_ts=${Date.now()}`;
            this._cancelEligibilityInflight = apiCallGet(url).finally(() => {
                this._cancelEligibilityInflight = null;
            });
        }

        const res = await this._cancelEligibilityInflight;
        if (!res?.success || !res?.data) {
            return null;
        }

        const payments = res.data.payments ?? null;
        const isBilled = Boolean(res.data.isBilled);
        const consolidatedBilling = res.data.consolidatedBilling ?? null;

        this.setState((prev) => {
            if (!prev?.surgery || prev.surgery.id !== surgeryId) {
                return null;
            }
            return {
                surgery: {
                    ...prev.surgery,
                    payments,
                    isBilled,
                    consolidatedBilling:
                        consolidatedBilling !== null
                            ? consolidatedBilling
                            : prev.surgery.consolidatedBilling,
                },
                sxBooking: {
                    ...prev.sxBooking,
                    isBilled,
                },
                cancelEligibilityError: null,
            };
        });

        try {
            if (typeof window !== "undefined" && window.__neoCache?.bookingById) {
                const prevCache = window.__neoCache.bookingById[surgeryId] || {};
                window.__neoCache.bookingById[surgeryId] = {
                    ...prevCache,
                    payments,
                    isBilled,
                    consolidatedBilling:
                        consolidatedBilling !== null
                            ? consolidatedBilling
                            : prevCache.consolidatedBilling,
                };
            }
        } catch (e) {}

        return res.data;
    } catch (err) {
        console.error("Failed to refresh payment/refund status for cancel:", err);
        const isBilledFlag = Boolean(this.state.surgery?.isBilled || this.state.sxBooking?.isBilled);
        if (isBilledFlag) {
            this.setState({
                cancelEligibilityError:
                    "Unable to verify payment/refund status. Please refresh and try again.",
            });
        }
        if (!silent) {
            cogoToast.error("Failed to verify payment/refund status");
        }
        return null;
    }
};

getNetPaymentPaiseOrNull = (paymentsSource) => {
    try {
        if (!paymentsSource) {
            return 0;
        }

        const toPaise = (entry) => {
            if (!entry) return 0;
            if (typeof entry.amountPaise === "number" && !Number.isNaN(entry.amountPaise)) {
                return Math.trunc(entry.amountPaise);
            }
            const amount = Number(entry.amount ?? entry.amountRupees ?? entry.amountPaise ?? 0);
            if (Number.isNaN(amount)) return 0;
            // Heuristic: if it's already an integer and large, assume paise; else rupees.
            if (Number.isInteger(amount) && amount > 9999) {
                return Math.trunc(amount);
            }
            return Math.trunc(amount * 100);
        };

        const accumulateFromEvents = (events) => {
            let paidPaise = 0;
            let refundedPaise = 0;
            (events || []).forEach((evt) => {
                const amountPaise = toPaise(evt);
                if (!amountPaise) return;
                const type = String(evt?.type || "").toLowerCase();
                if (type === "refund") {
                    refundedPaise += amountPaise;
                } else {
                    // Treat anything non-refund as payment (keeps older data compatible)
                    paidPaise += amountPaise;
                }
            });
            return paidPaise - refundedPaise;
        };

        if (Array.isArray(paymentsSource)) {
            if (paymentsSource.length === 0) return 0;
            return accumulateFromEvents(paymentsSource);
        }

        if (paymentsSource && typeof paymentsSource === "object") {
            if (Array.isArray(paymentsSource.events) && paymentsSource.events.length > 0) {
                return accumulateFromEvents(paymentsSource.events);
            }

            const hasTotals =
                typeof paymentsSource.totalPaidPaise === "number" ||
                typeof paymentsSource.totalRefundedPaise === "number" ||
                typeof paymentsSource.totalPaid === "number" ||
                typeof paymentsSource.totalRefunded === "number";
            if (!hasTotals) {
                return null;
            }

            const paidPaise =
                typeof paymentsSource.totalPaidPaise === "number"
                    ? Math.trunc(paymentsSource.totalPaidPaise)
                    : Math.trunc(Number(paymentsSource.totalPaid || 0) * 100);
            const refundedPaise =
                typeof paymentsSource.totalRefundedPaise === "number"
                    ? Math.trunc(paymentsSource.totalRefundedPaise)
                    : Math.trunc(Number(paymentsSource.totalRefunded || 0) * 100);
            return paidPaise - refundedPaise;
        }

        return 0;
    } catch (err) {
        console.error("Error calculating net payment (paise):", err);
        return null;
    }
};

getCancelRestrictionMessageFromNetPaise = (netPaise) => {
    const absRupees = (Math.abs(Number(netPaise || 0)) / 100).toFixed(2);
    if (netPaise > 0) {
        return `Cannot cancel surgery: Outstanding payment of ₹${absRupees} must be refunded first`;
    }
    if (netPaise < 0) {
        return `Cannot cancel surgery: Excess refund of ₹${absRupees} detected. Please reconcile payments.`;
    }
    return null;
};

getCancelDisableReason = () => {
    const status = (this.state.surgery?.status || "").toLowerCase();
    if (status === "cancelled") {
        return "Surgery is already cancelled";
    }

    if (this.state.isCheckingCancelEligibility) {
        return "Checking payment/refund status...";
    }

    const isBilledFlag = Boolean(this.state.surgery?.isBilled || this.state.sxBooking?.isBilled);
    if (isBilledFlag && this.state.cancelEligibilityError) {
        return this.state.cancelEligibilityError;
    }

    const netPaiseOrNull = this.getNetPaymentPaiseOrNull(this.state.surgery?.payments);
    if (netPaiseOrNull === null) {
        if (isBilledFlag) {
            return "Payment/refund status is not available yet. Please wait or open Billing to refresh.";
        }
        return null;
    }

    if (netPaiseOrNull !== 0) {
        return this.getCancelRestrictionMessageFromNetPaise(netPaiseOrNull);
    }

    return null;
};

 toLocalDay = (value) => {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
 };

 getPaymentEvents = (paymentsSource) => {
  if (Array.isArray(paymentsSource)) return paymentsSource;
  if (Array.isArray(paymentsSource?.events)) return paymentsSource.events;
  return [];
 };

 // `source` is the booking (local state or a fresh server copy).
 getPostponeDisableReason = (source = this.state.surgery) => {
  const events = this.getPaymentEvents(source?.payments);
  const netPaise = this.getNetPaymentPaiseOrNull(source?.payments) || 0;
  const paidPaise = events
   .filter((e) => String(e?.type || "").toLowerCase() !== "refund")
   .reduce((s, e) => s + Math.abs(Number(e?.amountPaise ?? Number(e?.amount || 0) * 100) || 0), 0);
  const billed = Boolean(source?.isBilled);

  if (billed && netPaise > 0) {
   return "Postpone is locked: payment has been collected and the surgery is billed.";
  }

  const sxDay = this.toLocalDay(source?.scheduledDate);
  const refundedOnSxDate = events.some(
   (e) =>
    String(e?.type || "").toLowerCase() === "refund" && sxDay && this.toLocalDay(e?.ts) === sxDay
  );
  if (paidPaise > 0 && netPaise === 0 && refundedOnSxDate) {
   return "Postpone is locked: the payment was fully refunded on the surgery date. Use Cancel Surgery instead.";
  }
  return null;
 };

 // Hard stops vs warnings. Key Events & People is a warning only — the user
 // can still confirm discharge after seeing the dialog.
 getDischargeChecks = (source) => {
  const blockers = [];
  const warnings = [];
  const sxDay = this.toLocalDay(source?.scheduledDate);
  const today = moment().format("YYYY-MM-DD");
  if (sxDay && sxDay > today) {
   blockers.push(
    `The surgery is scheduled for ${moment(sxDay).format("DD-MMM-YYYY")}. It cannot be discharged before that date.`
   );
  }
  const hasKeyEvents = Array.isArray(source?.keyEvents) && source.keyEvents.length > 0;
  const hasKeyPeople = Array.isArray(source?.keyPeople) && source.keyPeople.length > 0;
  if (!hasKeyEvents || !hasKeyPeople) {
   warnings.push("You are discharging patient without filling details of key people and events.");
  }
  const dischargeFields = [
   "dischargeDiagnosis",
   "reasonForAdmission",
   "investigation",
   "significantFindings",
   "dischargeOtNotes",
   "selectedTemplateContent",
  ];
  const hasDischargeDetails = dischargeFields.some((k) => String(source?.[k] || "").trim());
  if (!hasDischargeDetails) {
   warnings.push("The Discharge section has no details entered.");
  }
  return { blockers, warnings };
 };

 getDischargeBlockers = (source) => this.getDischargeChecks(source).blockers;

 // Cancel Surgery -> open reason dialog instead of immediate API call
 cancelSurgery = async () => {
  try {
   if (!this.state.surgery?.id) {
    cogoToast.error("Surgery not loaded");
    return;
   }

   if (!this.isValidObjectId(this.state.surgery.id)) {
    cogoToast.error("Invalid surgery ID");
    return;
   }

   const alreadyCancelled =
    (this.state.surgery?.status || "").toLowerCase() === "cancelled";
   if (alreadyCancelled) {
    cogoToast.info("Surgery is already cancelled");
    return;
   }

   const { ok } = await this.ensureFreshBeforeAction();
   if (!ok) return;

   this.setState({ isCheckingCancelEligibility: true, cancelEligibilityError: null });
   const fresh = await this.refreshSurgeryPaymentsFromServerForCancelCheck(this.state.surgery.id, {
        silent: true,
   });
   const paymentsSource = fresh?.payments ?? this.state.surgery?.payments;
   const netPaiseOrNull = this.getNetPaymentPaiseOrNull(paymentsSource);
   this.setState({ isCheckingCancelEligibility: false });

   if (netPaiseOrNull === null) {
        cogoToast.error("Cannot cancel: Payment/refund status could not be verified");
        return;
   }
   if (netPaiseOrNull !== 0) {
        cogoToast.error(this.getCancelRestrictionMessageFromNetPaise(netPaiseOrNull), {
            hideAfter: 6,
        });
        return;
   }

   this.setState({ showCancelDialog: true, cancelReason: "" });
  } catch (err) {
   console.error("Error opening cancel dialog:", err);
   this.setState({ isCheckingCancelEligibility: false });
  }
 };

 // View Discharge Summary: open print only (no status update, no audit)
 viewDischargeSummary = async () => {
  try {
   if (!this.state.surgery?.id) {
    cogoToast.error("Surgery not loaded");
    return;
   }

   if (!this.isValidObjectId(this.state.surgery.id)) {
    cogoToast.error("Invalid surgery ID");
    return;
   }

   const viewDischargeDisabledReason = this.getViewDischargeDisabledReason();
   if (viewDischargeDisabledReason) {
    cogoToast.warn(viewDischargeDisabledReason);
    return;
   }

   await this.ensureProcedureSummaryForCurrentSurgery();

   const printUrl = `${printApiUrl}/neo/sx/discharge/${this.state.surgery.id}`;
   window.open(printUrl, "_blank");
  } catch (err) {
   cogoToast.error("Failed to open Discharge Summary");
   console.error("Error opening discharge summary:", err);
  }
 };

 ensureProcedureSummaryForCurrentSurgery = async () => {
  try {
   const surgery = this.state.surgery || {};
   const sxBooking = this.state.sxBooking || {};
   const existing = String(
    surgery.procedureDischargeSummaryName ||
     sxBooking.procedureDischargeSummaryName ||
     ""
   ).trim();
   if (existing) return existing;

   const surgeries =
    surgery.selectedSurgeries ||
    surgery.surgeries ||
    sxBooking.selectedSurgeries ||
    sxBooking.surgeries ||
    [];
   const summary = buildProcedureDischargeSummaryName(surgeries);
   if (!summary) return "";

   const surgeryId = surgery.id || sxBooking.id;
   const prefix = this.props.authStore?.prefix;
   if (!surgeryId || !prefix) return summary;

   await apiCallPatch(`/v3/${prefix}/neoSxBookingMaster/update/${surgeryId}`, {
    procedureDischargeSummaryName: summary,
   });

   this.setState((prev) => ({
    surgery: prev.surgery
     ? { ...prev.surgery, procedureDischargeSummaryName: summary }
     : prev.surgery,
    sxBooking: prev.sxBooking
     ? { ...prev.sxBooking, procedureDischargeSummaryName: summary }
     : prev.sxBooking,
   }));

   try {
    if (
     typeof window !== "undefined" &&
     window.__neoCache?.bookingById &&
     surgeryId
    ) {
     window.__neoCache.bookingById[surgeryId] = {
      ...(window.__neoCache.bookingById[surgeryId] || {}),
      procedureDischargeSummaryName: summary,
     };
    }
   } catch (e) {}

   return summary;
  } catch (err) {
   console.error("Failed to ensure procedure summary:", err);
   return "";
  }
 };

 // Mark Discharged: update status + audit (no print open)
 markDischarged = async () => {
  try {
   if (!this.state.surgery?.id) {
    cogoToast.error("Surgery not loaded");
    return false;
   }

   if (!this.isValidObjectId(this.state.surgery.id)) {
    cogoToast.error("Invalid surgery ID");
    return false;
   }

   const currentOverallStatus = (
    this.state.surgery?.status ||
    this.state.sxBooking?.status ||
    ""
   ).toLowerCase();
   if (currentOverallStatus === "postponed") {
    cogoToast.warn("Postponed surgeries cannot be marked discharged");
    return false;
   }

   const isBilled = Boolean(
    this.state.surgery?.isBilled || this.state.sxBooking?.isBilled
   );
   if (!isBilled) {
    cogoToast.warn("Please bill the surgery before marking discharge");
    return false;
   }

   const { ok, fresh } = await this.ensureFreshBeforeAction();
   if (!ok) return false;
   const { blockers } = this.getDischargeChecks(
    fresh || {
     ...this.state.surgery,
     keyEvents: this.state.keyEvents,
     keyPeople: this.state.keyPeople,
    }
   );
   if (blockers.length) {
    this.setState({ dischargeBlockers: blockers });
    cogoToast.error(blockers[0], { hideAfter: 6 });
    return false;
   }

   const currentStatus =
    this.state.surgery?.status || this.state.sxBooking?.status || "";
   if (
    String(currentStatus || "")
     .trim()
     .toLowerCase() === "discharged"
   ) {
    cogoToast.info("Already marked as Discharged");
    return true;
   }

   this.setState({ isDischarging: true });
   const previousStatus = currentStatus;
   const response = await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${this.state.surgery.id}`,
    { status: "Discharged" }
   );

   if (response?.success) {
    this.setState((prevState) => ({
     surgery: { ...prevState.surgery, status: "Discharged" },
     sxBooking: { ...prevState.sxBooking, status: "Discharged" },
    }));

    // Keep cache in sync for immediate cross-page consistency
    try {
     const surgeryId = this.state.surgery?.id;
     if (surgeryId && window.__neoCache?.bookingById?.[surgeryId]) {
      window.__neoCache.bookingById[surgeryId] = {
       ...window.__neoCache.bookingById[surgeryId],
       status: "Discharged",
      };
     }
    } catch (e) {}

    cogoToast.success("Marked as Discharged");

    // Create audit log entry
    try {
     const resourceId = this.state.surgery?.id;
     const resourceName = `Surgery - ${
      this.state.surgery?.patientName || "Unknown Patient"
     }`;
     await apiCallPost(
      `/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`,
      [
       {
        resourceType: "SURGERY_BOOKING",
        resourceId,
        resourceName,
        actionType: "UPDATE",
        actionSlug: `Updated status: "${
         previousStatus || "Empty"
        }" → "Discharged"`,
        actionMetadata: {
         fieldName: "status",
         changeType: "UPDATE",
         previousValue: previousStatus || "",
         newValue: "Discharged",
        },
       },
      ]
     );
    } catch (e) {
     console.error("Audit log failed:", e);
    }
    return true;
   } else {
    throw new Error(response?.message || "Failed to update status");
   }
  } catch (err) {
   cogoToast.error("Failed to mark as Discharged");
   console.error("Error marking discharge:", err);
   return false;
  } finally {
   this.setState({ isDischarging: false });
  }
 };

 // Billing superuser: undo discharge — set lifecycle back to confirmed (PATCH + audit)
 revertDischargeToConfirmedAdmin = async () => {
  if (!this.hasSxBillingSuperuserAccess()) {
   cogoToast.warn("You do not have permission to change discharge status");
   return false;
  }
  if (!this.state.surgery?.id || !this.isValidObjectId(this.state.surgery.id)) {
   cogoToast.error("Surgery not loaded");
   return false;
  }
  if (this.isCancelledStatus()) {
   cogoToast.warn("Cannot change status for a cancelled booking this way");
   return false;
  }
  if (!this.isDischargedStatus()) {
   cogoToast.info("Booking is not discharged");
   return false;
  }
  try {
   this.setState({ isRevertingDischarge: true });
   const bookingId = this.state.surgery.id;
   const patientName = this.state.surgery?.patientName || "Unknown Patient";
   const previousStatus =
    this.state.surgery?.status || this.state.sxBooking?.status || "";
   const response = await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${bookingId}`,
    { status: "confirmed" }
   );
   if (response?.success) {
    this.setState((prevState) => ({
     surgery: { ...prevState.surgery, status: "confirmed" },
     sxBooking: { ...prevState.sxBooking, status: "confirmed" },
     auditRefreshTick: (prevState.auditRefreshTick || 0) + 1,
    }));
    try {
     if (bookingId && window.__neoCache?.bookingById?.[bookingId]) {
      window.__neoCache.bookingById[bookingId] = {
       ...window.__neoCache.bookingById[bookingId],
       status: "confirmed",
      };
     }
    } catch (e) {}
    cogoToast.success("Status set back to Confirmed");
    try {
     const resourceName = `Surgery - ${patientName}`;
     await apiCallPost(
      `/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`,
      [
       {
        resourceType: "SURGERY_BOOKING",
        resourceId: bookingId,
        resourceName,
        actionType: "UPDATE",
        actionSlug: `Admin: status "${previousStatus || "Empty"}" → "confirmed" (revert discharge)`,
        actionMetadata: {
         fieldName: "status",
         changeType: "ADMIN_UPDATE",
         previousValue: previousStatus || "",
         newValue: "confirmed",
         billingAdminOverride: true,
        },
       },
      ]
     );
    } catch (e) {
     console.error("Audit log failed (revert discharge):", e);
    }
    return true;
   }
   throw new Error(response?.message || "Failed to update status");
  } catch (err) {
   cogoToast.error("Failed to set status to Confirmed");
   console.error(err);
   return false;
  } finally {
   this.setState({ isRevertingDischarge: false });
  }
 };

 confirmRevertDischargeToConfirmed = async () => {
  if (
   !window.confirm(
    "Set this booking back to Confirmed? This reverses discharge for workflow/editing. Use only when appropriate."
   )
  ) {
   return;
  }
  await this.revertDischargeToConfirmedAdmin();
 };

 // Systemic Params Tab Methods
 handleSystemicUpdate = (systemicInvestigations) => {
  this.setState({ systemicInvestigations });
 };

 // Key People & Events handlers
 handleKeyPeopleUpdate = (keyPeople) => {
  this.setState({ keyPeople });
 };

 handleKeyEventsUpdate = (keyEvents) => {
  this.setState({ keyEvents });
 };

 handleFileInputChange = (e) => {
  const file = e.target.files?.[0] || null;
  this.setState({ newFileBlob: file });
  if (file) {
   setTimeout(() => this.uploadFile(), 100);
  }
 };

 openPreview = (file) =>
  this.setState({ previewFile: file, isPreviewOpen: true });
 closePreview = () =>
  this.setState({ isPreviewOpen: false, previewFile: null });

 // Cancel: dialog controls
 openCancelDialog = () => {
  this.setState({ showCancelDialog: true, cancelReason: "" });
 };

 closeCancelDialog = () => {
  this.setState({
   showCancelDialog: false,
   cancelReason: "",
   isSubmittingCancel: false,
  });
 };

 // Mark Discharged: dialog controls (separate from cancel)
 openMarkDischargedDialog = async () => {
  this.setState({
   showMarkDischargedDialog: true,
   isSubmittingMarkDischarged: false,
   isCheckingDischarge: true,
   dischargeBlockers: [],
   dischargeWarnings: [],
  });
  const { ok, fresh } = await this.ensureFreshBeforeAction();
  if (!ok) {
   this.setState({ showMarkDischargedDialog: false, isCheckingDischarge: false });
   return;
  }
  const { blockers, warnings } = this.getDischargeChecks(
   fresh || {
    ...this.state.surgery,
    keyEvents: this.state.keyEvents,
    keyPeople: this.state.keyPeople,
   }
  );
  this.setState({
   dischargeBlockers: blockers,
   dischargeWarnings: warnings,
   isCheckingDischarge: false,
  });
 };

 closeMarkDischargedDialog = () => {
  this.setState({
   showMarkDischargedDialog: false,
   isSubmittingMarkDischarged: false,
  });
 };

 confirmMarkDischarged = async () => {
  try {
   this.setState({ isSubmittingMarkDischarged: true });
   const ok = await this.markDischarged();
   if (ok) {
    this.closeMarkDischargedDialog();
   }
  } catch (e) {
   // markDischarged handles its own errors/toasts
  } finally {
   this.setState({ isSubmittingMarkDischarged: false });
  }
 };

 handleCancelReasonChange = (e) => {
  const value = e?.target?.value ?? "";
  this.setState({ cancelReason: value });
 };

 submitCancellation = async () => {
  try {
   if (!this.state.surgery?.id) {
    cogoToast.error("Surgery not loaded");
    return;
   }

   if (!this.isValidObjectId(this.state.surgery.id)) {
    cogoToast.error("Invalid surgery ID");
    return;
   }

   const reason = (this.state.cancelReason || "").trim();
   if (!reason) {
    cogoToast.warn("Please enter a cancellation reason");
    return;
   }

   // Re-validate payment/refund status from server before submitting
   this.setState({ isCheckingCancelEligibility: true, cancelEligibilityError: null });
   const fresh = await this.refreshSurgeryPaymentsFromServerForCancelCheck(this.state.surgery.id, {
        silent: true,
   });
   const paymentsSource = fresh?.payments ?? this.state.surgery?.payments;
   const netPaiseOrNull = this.getNetPaymentPaiseOrNull(paymentsSource);
   this.setState({ isCheckingCancelEligibility: false });

   if (netPaiseOrNull === null) {
        cogoToast.error("Cannot cancel: Payment/refund status could not be verified");
        this.setState({ isSubmittingCancel: false, showCancelDialog: false });
        return;
   }
   if (netPaiseOrNull !== 0) {
        cogoToast.error(this.getCancelRestrictionMessageFromNetPaise(netPaiseOrNull), {
            hideAfter: 6,
        });
        this.setState({ isSubmittingCancel: false, showCancelDialog: false });
        return;
   }

   this.setState({ isSubmittingCancel: true });

   const response = await apiCallPatch(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/cancel/${this.state.surgery.id}`,
    { status: "Cancelled", cancellationReason: reason }
   );

   if (response?.success) {
    cogoToast.success("Surgery cancelled successfully");
    this.setState((prevState) => ({
     surgery: {
      ...prevState.surgery,
      status: "Cancelled",
      cancellationReason: reason,
     },
     sxBooking: { ...prevState.sxBooking, status: "Cancelled" },
     isCancelling: false,
     isSubmittingCancel: false,
     showCancelDialog: false,
    }));

    // Update cache as well
    try {
     const surgeryId = this.state.surgery?.id;
     if (surgeryId && window.__neoCache?.bookingById?.[surgeryId]) {
      window.__neoCache.bookingById[surgeryId] = {
       ...window.__neoCache.bookingById[surgeryId],
       status: "Cancelled",
       cancellationReason: reason,
      };
     }
    } catch (e) {
     // Non-blocking cache update
    }
   } else {
    throw new Error(response?.message || "Failed to cancel surgery");
   }
  } catch (err) {
   cogoToast.error("Failed to cancel surgery");
   console.error("Error cancelling surgery:", err);
   this.setState({ isSubmittingCancel: false });
   this.setState({ isCheckingCancelEligibility: false });
  }
 };

 // Consumption Tab Methods (simplified - extract the key methods)
 loadLocations = async () => {
  try {
   this.setState({ isLoadingLocations: true });
   const response = await apiCallPost(
    `/v3/${this.props.authStore.prefix}/locations/getAll`,
    {
     purpose: "literallyAllLocations",
    }
   );
   if (response.success) {
    const entities = Array.isArray(response.data?.entities)
     ? response.data.entities
     : [];
    const sorted = entities.slice().sort((a, b) => {
     const aBranch = (a?.branchName || "").toString().toLowerCase();
     const bBranch = (b?.branchName || "").toString().toLowerCase();
     if (aBranch !== bBranch) return aBranch.localeCompare(bBranch);
     const aLoc = (a?.locationName || "").toString().toLowerCase();
     const bLoc = (b?.locationName || "").toString().toLowerCase();
     return aLoc.localeCompare(bLoc);
    });
    this.setState({
     availableLocations: sorted,
     isLoadingLocations: false,
    });
   }
  } catch (err) {
   console.error("Error loading locations:", err);
   this.setState({ isLoadingLocations: false });
  }
 };

 searchStockItems = async () => {
  try {
   if (
    !this.state.selectedLocation ||
    !this.state.consumptionSearchTerm.trim()
   ) {
    return;
   }
   this.setState({ isSearchingConsumption: true });
   const response = await apiCallPost(
    `/v3/${this.props.authStore.prefix}/sxItemConsumption/searchStockItem`,
    {
     searchTerm: this.state.consumptionSearchTerm.trim(),
     locationId: this.state.selectedLocation._id,
     branchName: this.state.surgery?.branchName,
    }
   );
   if (response.success) {
    this.setState({ consumptionSearchResults: response.data || [] });
   }
  } catch (err) {
   console.error("Error searching stock items:", err);
  } finally {
   this.setState({ isSearchingConsumption: false });
  }
 };

 // Additional consumption methods...
 handleLocationChange = (location) => {
  this.setState({ selectedLocation: location });
 };

 handleSearchTermChange = (term) => {
  this.setState({ consumptionSearchTerm: term });
 };

 clearSearchResults = () => {
  this.setState({
   consumptionSearchResults: [],
   consumptionSearchTerm: "",
   selectedConsumptionItems: [],
  });
 };

 addItemToConsumption = (item) => {
  const isAlreadySelected = this.state.selectedConsumptionItems.some(
   (selected) => selected._id === item._id
  );
  if (!isAlreadySelected) {
   this.setState((prevState) => ({
    selectedConsumptionItems: [...prevState.selectedConsumptionItems, item],
   }));
  }
 };

 removeItemFromConsumption = (itemId) => {
  this.setState((prevState) => ({
   selectedConsumptionItems: prevState.selectedConsumptionItems.filter(
    (item) => item._id !== itemId
   ),
  }));
 };

 openConsumptionDialog = () => {
  if (this.state.selectedConsumptionItems.length === 0) {
   cogoToast.warn("Please select at least one item for consumption");
   return;
  }
  this.setState({
   showConsumptionDialog: true,
   consumptionForm: {
    ...this.state.consumptionForm,
    consumingPatientId:
     this.state.surgery?.patientOriginalData?._id ||
     this.state.surgery?.patientId ||
     "",
    consumingPatientName: this.state.surgery?.patientName || "",
    consumingDoctorId: this.state.surgery?.doctorId || "",
    consumingDoctorName: this.state.surgery?.doctorName || "",
   },
  });
 };

 closeConsumptionDialog = () => {
  this.setState({
   showConsumptionDialog: false,
   selectedConsumptionItems: [],
   consumptionSearchResults: [],
   consumptionSearchTerm: "",
  });
 };

 updateConsumptionForm = (field, value) => {
  this.setState((prevState) => ({
   consumptionForm: {
    ...prevState.consumptionForm,
    [field]: value,
   },
  }));
 };

 createConsumption = async () => {
  try {
   this.setState({ isCreatingConsumption: true });
   if (
    !this.state.consumptionForm.consumptionHead ||
    this.state.selectedConsumptionItems.length === 0
   ) {
    cogoToast.warn("Please fill required fields");
    return;
   }

   const consumedItemIds = this.state.selectedConsumptionItems.map(
    (item) => item._id
   );
   const patientId = this.state.surgery?.patientOriginalData?._id;
   const doctorId =
    this.state.surgery?.doctorId ||
    this.state.consumptionForm.consumingDoctorId;

   const response = await apiCallPost(
    `/v3/${this.props.authStore.prefix}/sxItemConsumption/create`,
    {
     consumedItems: consumedItemIds,
     consumptionDate: this.state.consumptionForm.consumptionDate.toISOString(),
     consumingDoctorId: doctorId,
     consumingDoctorName: this.state.consumptionForm.consumingDoctorName,
     consumingPatientId: patientId,
     consumingPatientName: this.state.consumptionForm.consumingPatientName,
     consumptionHead: this.state.consumptionForm.consumptionHead,
     consumptionOT: this.state.consumptionForm.consumptionOT,
     surgeryBookingId: this.state.surgery?.id,
     notes: this.state.consumptionForm.notes,
     requestedConsumptions: this.state.consumptionForm.requestedConsumptions,
    }
   );

   if (response.success) {
    cogoToast.success("Items consumed successfully");

    // Debug: Log the response to see data structure
    console.log("Create consumption response:", response);

    this.closeConsumptionDialog();

    // Clear the cache to force fresh data
    const surgeryId = this.state.surgery?.id;
    if (surgeryId && window.__neoCache?.bookingById?.[surgeryId]) {
     delete window.__neoCache.bookingById[surgeryId];
     console.log("Cache cleared for surgery:", surgeryId);
    }

    // Clear any inflight requests
    if (surgeryId && window.__neoInflight?.bookingById?.[surgeryId]) {
     delete window.__neoInflight.bookingById[surgeryId];
    }

    // Clear old consumptions from state to force fresh load
    this.setState({ itemConsumptions: [] });

    // Reload the entire surgery details to get fresh data
    await this.loadSurgeryDetails();

    // Force load consumptions again to ensure we have the latest data
    await this.loadItemConsumption();

    // Set active tab back to consumption after data is loaded
    this.setState({ activeTab: "consumption" });

    console.log(
     "Surgery details and consumptions reloaded, switched to consumption tab"
    );
   }
  } catch (err) {
   console.error("Error creating consumption:", err);
   cogoToast.error("Failed to create consumption");
  } finally {
   this.setState({ isCreatingConsumption: false });
  }
 };

 render() {
  const { surgery, isLoading, error, activeTab } = this.state;

    const statusValue = (surgery?.status || this.state.sxBooking?.status || "").toLowerCase();
    const isBilledFlag = Boolean(surgery?.isBilled || this.state.sxBooking?.isBilled);
    const isCancelled = statusValue === "cancelled";
    const isScheduleLocked = (statusValue === "discharged" && isBilledFlag) || isCancelled;
    const isPostponed = statusValue === "postponed";
    const cancelDisableReason = this.getCancelDisableReason();
    const viewDischargeDisabledReason = this.getViewDischargeDisabledReason();
    const isViewDischargeDisabled = Boolean(viewDischargeDisabledReason);

  if (isLoading) {
   return (
    <div
     style={{
      padding: "20px",
      backgroundColor: "var(--bg-primary)",
      color: "var(--text-primary)",
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
     }}
    >
     <NonIdealState
      icon={<Spinner />}
      title="Loading Surgery Details..."
      description="Please wait while we load the surgery information"
     />
    </div>
   );
  }

  if (error) {
   return (
    <div
     style={{
      padding: "20px",
      backgroundColor: "var(--bg-primary)",
      color: "var(--text-primary)",
      minHeight: "100vh",
     }}
    >
     <NonIdealState
      icon="error"
      title="Error Loading Surgery Details"
      description={error}
      action={
       <div style={{ display: "flex", gap: "10px" }}>
        <Button intent="primary" onClick={this.loadSurgeryDetails}>
         Retry
        </Button>
        <Button onClick={this.goToSurgeryList}>Back to Surgery List</Button>
       </div>
      }
     />
    </div>
   );
  }

  if (!surgery) {
   return (
    <div
     style={{
      padding: "20px",
      backgroundColor: "var(--bg-primary)",
      color: "var(--text-primary)",
      minHeight: "100vh",
     }}
    >
     <NonIdealState
      icon="search"
      title="Surgery Not Found"
      description="The requested surgery booking could not be found"
      action={
       <Button intent="primary" onClick={this.goToSurgeryList}>
        Back to Surgery List
       </Button>
      }
     />
    </div>
   );
  }

  return (
   <div
    style={{
     padding: "20px",
     paddingTop: "0",
     backgroundColor: "var(--bg-primary)",
     color: "var(--text-primary)",
     minHeight: "100vh",
    }}
   >
    {/* Header */}
    <div
     style={{
      position: "sticky",
      zIndex: 10,
      top: "0",
      backgroundColor: "var(--bg-primary)",
      paddingTop: "20px",
      paddingBottom: "10px",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      marginBottom: "0px",
     }}
    >
     <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
      <Button
       minimal
       large
       icon="arrow-left"
       onClick={this.goBack}
       style={{ color: "var(--text-secondary)" }}
      />
      <div>
       {/* Patient and Surgery Details - 4x3 Grid Layout */}
       <div
        style={{
         display: "grid",
         gridTemplateColumns: "1fr 1fr 1fr",
         gap: "15px",
         marginBottom: "10px",
         padding: "0",
        }}
       >
        {/* Column 1: Patient Info */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
         {!this.state.isEditingDetails ? (
          <Tag large minimal style={{ fontSize: "16px" }}>
           Name: {surgery.patientName || "N/A"}
          </Tag>
         ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
           <Tag large minimal style={{ fontSize: "16px" }}>
            Name:
           </Tag>
           <InputGroup
            placeholder="Enter patient name"
            value={this.state.editPatientName || ""}
            onChange={(e) => this.setState({ editPatientName: e.target.value })}
            fill
           />
          </div>
         )}
         <Tag large minimal style={{ fontSize: "16px" }}>
          PID: {surgery.patientGenericPid || "N/A"}
         </Tag>
         {!this.state.isEditingDetails ? (
          <Tag large minimal style={{ fontSize: "16px" }}>
           Age/Sex: {surgery.patientAge ? `${surgery.patientAge}y` : "N/A"} /{" "}
           {(() => {
            const v = surgery.patientSex;
            const s = typeof v === "string" ? v.trim().toLowerCase() : "";
            const label =
             s === "m" || s === "male"
              ? "Male"
              : s === "f" || s === "female"
              ? "Female"
              : v || "";
            return label || "N/A";
           })()}
          </Tag>
         ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
           <Tag large minimal style={{ fontSize: "16px" }}>
            DOB & Sex:
           </Tag>
           <DateInput
            value={this.state.editDob}
            onChange={(d) => {
             const minDob = new Date(1900, 0, 1);
             const maxDob = new Date();
             this.setState({ editDob: this.clampDate(d, minDob, maxDob) });
            }}
            placeholder="Select DOB"
            formatDate={(d) => (d ? moment(d).format("DD MMM YYYY") : "")}
            parseDate={(str) =>
             str ? moment(str, ["DD MMM YYYY", "YYYY-MM-DD"]).toDate() : null
            }
            highlightCurrentDay
            popoverProps={{ minimal: true }}
            minDate={new Date(1900, 0, 1)}
            maxDate={new Date()}
           />
           <HTMLSelect
            value={this.state.editSex || ""}
            onChange={(e) => this.setState({ editSex: e.target.value })}
            options={["Male", "Female", "Other"]}
           />
          </div>
         )}
         {!this.state.isEditingDetails ? (
          <Tag large minimal style={{ fontSize: "16px" }}>
           Phone: {surgery.patientPhone || "N/A"}
          </Tag>
         ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
           <Tag large minimal style={{ fontSize: "16px" }}>
            Phone:
           </Tag>
           <InputGroup
            placeholder="Enter phone number"
            value={this.state.editPatientPhone || ""}
            onChange={(e) =>
             this.setState({ editPatientPhone: e.target.value })
            }
            fill
           />
          </div>
         )}
         {this.state.isEditingDetails ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
           <Tag large minimal style={{ fontSize: "16px" }}>
            Address:
           </Tag>
           <InputGroup
            placeholder="Enter address"
            value={this.state.editAddress || ""}
            onChange={(e) => this.setState({ editAddress: e.target.value })}
            fill
           />
          </div>
         ) : surgery.patientAddress || surgery.patientOriginalData?.address ? (
          <Tag
           large
           minimal
           style={{
            fontSize: "16px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
           }}
           title={
            surgery.patientAddress || surgery.patientOriginalData?.address
           }
          >
           Address:{" "}
           {surgery.patientAddress || surgery.patientOriginalData?.address}
          </Tag>
         ) : null}
        </div>

        {/* Column 2: Surgery & Medical Info */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
         <div
          ref={this.headerRowHeightRef}
          style={{ display: "block", width: "100%" }}
         >
          <Tag
           large
           minimal
           interactive
           role="button"
           title="Click to copy booking reference"
           onClick={() => this.copyToClipboard(surgery.bookingReference)}
           style={{ fontSize: "16px", width: "100%", display: "block" }}
          >
           ID: {surgery.bookingReference}
          </Tag>
         </div>
         <Tag large minimal style={{ fontSize: "16px" }}>
          Laterality:{" "}
          {(() => {
           const surgeries = surgery?.selectedSurgeries || [];
           if (surgeries.length === 0) {
            return surgery?.eye || "Not specified";
           }

           // Get unique lateralities from all surgeries
           const lateralities = [
            ...new Set(
             surgeries.map((s) => s.laterality || s.eye).filter(Boolean)
            ),
           ];

           if (lateralities.length === 0) {
            return "Not specified";
           } else if (lateralities.length === 1) {
            const lat = lateralities[0].toLowerCase();
            if (lat === "left" || lat === "le" || lat === "os") {
             return "Left Eye";
            } else if (lat === "right" || lat === "re" || lat === "od") {
             return "Right Eye";
            } else {
             return lateralities[0];
            }
           } else {
            return "Both Eyes";
           }
          })()}
         </Tag>
         <Tag large minimal style={{ fontSize: "16px" }}>
          Surgeon: {this.state.sxBooking.surgeon || surgery.doctorName || "N/A"}
         </Tag>
         <Tag large minimal style={{ fontSize: "16px" }}>
          Anesthesia:{" "}
          {surgery.anesthesia || this.state.sxBooking.anesthesia || "N/A"}
         </Tag>
         {/* IOL Information */}
         {(() => {
          const iolItems =
           surgery?.IOLitems || this.state.sxBooking?.IOLitems || [];
          if (iolItems.length === 0) {
           return (
            <Tag large minimal style={{ fontSize: "16px" }}>
             IOL: Not selected
            </Tag>
           );
          }

          // Display IOL items with their variants
          const iolDisplay = iolItems
           .map((iol, index) => {
            let displayText = iol.itemName || "Unknown IOL";

            // Add variant information if available
            if (
             iol.selectedVariants &&
             Object.keys(iol.selectedVariants).length > 0
            ) {
             const variants = Object.entries(iol.selectedVariants)
              .filter(([key, value]) => value && value.trim())
              .map(([key, value]) => value)
              .join(", ");
             if (variants) {
              displayText += ` (${variants})`;
             }
            }

            return displayText;
           })
           .join("; ");

          return (
           <Tag
            large
            minimal
            style={{
             fontSize: "16px",
             overflow: "hidden",
             textOverflow: "ellipsis",
             whiteSpace: "nowrap",
            }}
            title={`IOL: ${iolDisplay}`}
           >
            IOL: {iolDisplay}
           </Tag>
          );
         })()}
        </div>

        {/* Column 3: Procedure & Location Info */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
         {(() => {
          const surgeries = surgery?.selectedSurgeries || [];
          if (surgeries.length === 0)
           return (
            <Tag large minimal style={{ fontSize: "16px" }}>
             Procedure: N/A
            </Tag>
           );
          const surgeryNames = surgeries
           .map((s) => s.name || s.printedName || "Unknown")
           .filter(Boolean);
          if (surgeryNames.length === 0)
           return (
            <Tag large minimal style={{ fontSize: "16px" }}>
             Procedure: N/A
            </Tag>
           );
          return (
           <Tag
            large
            minimal
            style={{
             fontSize: "16px",
             overflow: "hidden",
             textOverflow: "ellipsis",
             whiteSpace: "nowrap",
            }}
            title={surgeryNames.join(", ")}
           >
            Procedure: {surgeryNames.join(", ")}
           </Tag>
          );
         })()}
         <Tag large minimal style={{ fontSize: "16px" }}>
          Branch: {surgery.branchName || "N/A"}
         </Tag>
         <Tag large minimal style={{ fontSize: "16px" }}>
          Surgery Date:{" "}
          {surgery.scheduledDate
           ? `${moment(surgery.scheduledDate).format("DD MMM YYYY")}${
              surgery.scheduledTime ? `, ${surgery.scheduledTime}` : ""
             }`
           : "N/A"}
         </Tag>
         <Tag large minimal style={{ fontSize: "16px" }}>
          Payor Panel:{" "}
          {this.state.sxBooking?.payorPanel ||
           surgery?.payorPanel ||
           (surgery?.selectedSurgeries || [])[0]?.payor ||
           "N/A"}
         </Tag>
        </div>
       </div>
      </div>
     </div>

     <div
      style={{
       display: "grid",
       gridTemplateRows: `repeat(5, ${this.state.headerRowHeight || 36}px)`,
       rowGap: "8px",
       minWidth: "220px",
      }}
     >
      {(() => {
       const rawStatus = surgery?.status || this.state.sxBooking?.status || "-";
       const statusMeta = this.getStatusMeta(rawStatus);
       const billed =
        Boolean(surgery?.isBilled || this.state.sxBooking?.isBilled) ||
        String(rawStatus || "")
         .trim()
         .toLowerCase() === "billed";
       const billedPalette = billed
        ? {
           color: "#0F9960",
           background: "rgba(15, 153, 96, 0.12)",
           border: "rgba(15, 153, 96, 0.45)",
           label: "Billed",
          }
        : {
           color: "#DB3737",
           background: "rgba(219, 55, 55, 0.12)",
           border: "rgba(219, 55, 55, 0.45)",
           label: "Unbilled",
          };
       return (
        <div style={{ display: "flex", gap: "8px", marginLeft: "8px" }}>
         <Tag
          large
          style={{
           flex: 1,
           color: statusMeta.color,
           backgroundColor: statusMeta.background,
           border: `1px solid ${statusMeta.border}`,
           display: "flex",
           alignItems: "center",
           justifyContent: "center",
           fontWeight: 600,
          }}
         >
          {statusMeta.label}
         </Tag>
         <Tag
          large
          style={{
           flex: 1,
           color: billedPalette.color,
           backgroundColor: billedPalette.background,
           border: `1px solid ${billedPalette.border}`,
           display: "flex",
           alignItems: "center",
           justifyContent: "center",
           fontWeight: 600,
          }}
         >
          {billedPalette.label}
         </Tag>
        </div>
       );
      })()}
      {String(surgery?.status || "").toLowerCase() !== "cancelled" ? (
       <>
        {(() => {
         const billedFlag = Boolean(
          surgery?.isBilled || this.state.sxBooking?.isBilled
         );
         return (
          <div
           onClick={() => {
            if (billedFlag) {
             cogoToast.warn("Cannot Edit Details for Billed Surgery");
            }
           }}
           style={{ width: "100%", height: "100%", marginLeft: "8px" }}
          >
           <AnchorButton
            intent={this.state.isEditingDetails ? "success" : "none"}
            icon={this.state.isEditingDetails ? "tick" : "edit"}
            outlined
            disabled={billedFlag}
            onClick={(e) => {
             if (billedFlag) {
              e.preventDefault();
              return;
             }
             this.toggleEditDetails();
            }}
            style={{ width: "100%", height: "100%" }}
            loading={this.state.isSavingPatientDetails}
           >
            {this.state.isEditingDetails ? "Done" : "Edit Patient Details"}
           </AnchorButton>
          </div>
         );
        })()}
        <AnchorButton
         intent="none"
         icon="credit-card"
         outlined
         target="_blank"
         href={`${printApiUrl}/neo/sx/bedHeadChecklist/${surgery.id}`}
         style={{ width: "100%", height: "100%", marginLeft: "8px" }}
        >
         Bed Head Ticket
        </AnchorButton>
        {(() => {
            const billedFlag = Boolean(surgery?.isBilled || this.state.sxBooking?.isBilled);
            return (
                <div style={{ width: "100%", height: "100%", marginLeft: "8px" }}>
                    <div
                        style={{
                            display: "flex",
                            gap: "8px",
                            width: "100%",
                            height: "100%",
                        }}
                    >
                        <div style={{ width: "100%", height: "100%" }}>
                            <AnchorButton
                                intent="none"
                                icon="document"
                                outlined
                                disabled={isViewDischargeDisabled}
                                onClick={this.viewDischargeSummary}
                                style={{ width: "100%", height: "100%" }}
                                title={viewDischargeDisabledReason || undefined}
                            >
                                View Discharge
                            </AnchorButton>
                        </div>

                        <div
                            onClick={() => {
                                if (isPostponed) {
                                    cogoToast.warn(
                                        "Postponed surgeries cannot be marked discharged"
                                    );
                                    return;
                                }
                                if (!billedFlag) {
                                    cogoToast.warn(
                                        "Surgery needs to be billed to mark Discharged"
                                    );
                                }
                            }}
                            style={{ width: "100%", height: "100%" }}
                        >
                            <Button
                                intent="none"
                                icon="confirm"
                                outlined
                                disabled={!billedFlag || this.state.isDischarging || isPostponed}
                                loading={this.state.isDischarging}
                                onClick={(e) => {
                                    if (!billedFlag || isPostponed) {
                                        e.preventDefault();
                                        if (isPostponed) {
                                            cogoToast.warn("Postponed surgeries cannot be marked discharged");
                                        }
                                        return;
                                    }
                                    this.openMarkDischargedDialog();
                                }}
                                style={{ width: "100%", height: "100%" }}
                                title={
                                    isPostponed
                                        ? "Postponed surgeries cannot be marked discharged"
                                        : undefined
                                }
                            >
                                Mark Discharged
                            </Button>
                        </div>
                        {this.hasSxBillingSuperuserAccess() &&
                            String(
                                surgery?.status ||
                                    this.state.sxBooking?.status ||
                                    ""
                            )
                                .trim()
                                .toLowerCase() === "discharged" &&
                            !this.isCancelledStatus() && (
                                <div style={{ width: "100%", height: "100%" }}>
                                    <Button
                                        intent="warning"
                                        icon="undo"
                                        outlined
                                        loading={this.state.isRevertingDischarge}
                                        onClick={this.confirmRevertDischargeToConfirmed}
                                        style={{ width: "100%", height: "100%" }}
                                        title="Billing admin: set status back to Confirmed"
                                    >
                                        Admin: Confirmed
                                    </Button>
                                </div>
                            )}
                    </div>
                </div>
            );
          })()}
        {!isScheduleLocked && (
            <Button
                intent="danger"
                icon="cross"
                outlined
                loading={this.state.isCancelling || this.state.isCheckingCancelEligibility}
                disabled={this.state.isCancelling || Boolean(cancelDisableReason)}
                onClick={this.cancelSurgery}
                title={cancelDisableReason || "Cancel this surgery"}
                style={{ width: "100%", height: "100%", marginLeft: "8px" }}
            >
                Cancel Surgery
            </Button>
        )}
       </>
      ) : (
       <div style={{ gridRow: "2 / span 4" }}>
        <Card
         elevation={Elevation.ONE}
         style={{
          margin: "10px",
          backgroundColor: "var(--bg-surface)",
          width: "100%",
          height: "75%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
         }}
        >
         <div style={{ color: "var(--text-secondary)" }}>
          {" "}
          Cancellation Reason: <br />
          {this.state.surgery?.cancellationReason
           ? this.state.surgery.cancellationReason
           : "No reason provided"}
         </div>
        </Card>
       </div>
      )}
     </div>
    </div>

    {/* Main Content with Tabs */}
    <Card elevation={Elevation.TWO} style={{ padding: "0" }}>
     <Tabs
      id="surgery-booking-tabs"
      selectedTabId={activeTab}
      onChange={this.handleTabChange}
      renderActiveTabPanelOnly={true}
      large
     >
      {/* Tab 1: SX Booking Details */}
      <Tab
       id="sx-booking"
       title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
         <Icon icon={IconNames.CALENDAR} />
         <span>SX Booking Details</span>
        </div>
       }
       panel={
        <SxBookingDetailsTab
         surgery={surgery}
         sxBooking={this.state.sxBooking}
         consultants={this.state.consultants}
         isLoadingConsultants={this.state.isLoadingConsultants}
         anesthesiaTypes={this.state.anesthesiaTypes}
         anesthesiaOptions={this.state.anesthesiaOptions}
         isLoadingAnesthesiaTypes={this.state.isLoadingAnesthesiaTypes}
         isSaving={this.state.isSaving}
         isSavingMedicalTeam={this.state.isSavingMedicalTeam}
         isSavingSchedule={this.state.isSavingSchedule}
         isSavingClinicalDetails={this.state.isSavingClinicalDetails}
         originalSxBooking={this.state.originalSxBooking}
         isMedicalTeamDirty={this.state.isMedicalTeamDirty}
         isScheduleDirty={this.state.isScheduleDirty}
         isClinicalDetailsDirty={this.state.isClinicalDetailsDirty}
         isNotesDirty={this.state.isNotesDirty}
         isScheduleLocked={isScheduleLocked}
         isPostponingSurgery={this.state.isPostponingSurgery}
         onUpdateField={this.updateSxBookingField}
         onSurgeonChange={this.handleSurgeonChange}
         onSecondarySurgeonChange={this.handleSecondarySurgeonChange}
         onAdvisedByChange={this.handleAdvisedByChange}
         onAnaesthetistChange={this.handleAnaesthetistChange}
         onClearMedicalTeamField={this.clearMedicalTeamField}
         onSurgeonDropdownClick={this.handleSurgeonDropdownClick}
         onPostponeSurgery={this.postponeSurgery}
         postponeDisabledReason={this.getPostponeDisableReason()}
         onAddSurgery={this.addSurgery}
         onRemoveSurgery={this.removeSurgery}
         onUpdateSurgeries={this.onUpdateSurgeries}
         onSave={this.saveSurgeryDetails}
         onSaveMedicalTeam={this.saveMedicalTeam}
         onSaveSchedule={this.saveSchedule}
         onSaveClinicalDetails={this.saveClinicalDetails}
         onIOLItemsChanged={this.handleIOLItemsChanged}
         canEdit={!this.isDischargedStatus() && !this.isCancelledStatus()}
         allowPayorPanelOverride={this.hasSxBillingSuperuserAccess()}
         onPayorPanelCommit={this.applyPayorPanelOverride}
         payorPanelOverrideSaving={this.state.isSavingPayorPanelOverride}
        />
       }
      />

      {/* Tab 2: Systemic Parameters & Checklists */}
      {/* <Tab
              id="systemic-params"
              title={
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Icon icon={IconNames.CLIPBOARD} />
                  <span>Systemic Parameters</span>
                </div>
              }
              panel={
                <SystemicParamsTab
                  surgery={surgery}
                  systemicInvestigations={this.state.systemicInvestigations}
                  files={this.state.files}
                  isLoadingFiles={this.state.isLoadingFiles}
                  isUploadingFile={this.state.isUploadingFile}
                  uploadProgressDone={this.state.uploadProgressDone}
                  uploadProgressTotal={this.state.uploadProgressTotal}
                  newFileBlob={this.state.newFileBlob}
                  previewFile={this.state.previewFile}
                  isPreviewOpen={this.state.isPreviewOpen}
                  onSystemicUpdate={this.handleSystemicUpdate}
                  onFileInputChange={this.handleFileInputChange}
                  onOpenPreview={this.openPreview}
                  onClosePreview={this.closePreview}
                  onDeleteFile={this.deleteFile}
                />
              }
            /> */}

      {/* Tab 3: Key Events & Key People */}
      <Tab
       id="key-events"
       title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
         <Icon icon={IconNames.TIMELINE_EVENTS} />
         <span>Key Events & People</span>
        </div>
       }
       panel={
        <KeyEventsTab
         data={surgery}
         keyEvents={this.state.keyEvents}
         keyPeople={this.state.keyPeople}
         onKeyEventsUpdate={this.handleKeyEventsUpdate}
         onKeyPeopleUpdate={this.handleKeyPeopleUpdate}
         canEdit={!this.isDischargedStatus() && !this.isCancelledStatus()}
        />
       }
      />

      {/* Tab 4: Surgery Video */}
      {/* <Tab
              id="surgery-video"
              title={
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Icon icon={IconNames.VIDEO} />
                  <span>Surgery Video</span>
                </div>
              }
              panel={<SurgeryVideoTab />}
            /> */}

      {/* Tab 5: Discharge */}
      <Tab
       id="discharge"
       title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
         <Icon icon={IconNames.ARROW_RIGHT} />
         <span>Discharge</span>
        </div>
       }
       panel={
        <DischargeTab
         surgery={surgery}
         onDischargeUpdate={this.updateDischargeData}
         canEdit={!this.isCancelledStatus()}
        />
       }
      />

      {/* Tab 6: Consumption */}
      <Tab
       id="consumption"
       title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
         <Icon icon={IconNames.BOX} />
         <span>Consumption</span>
        </div>
       }
       panel={
        <ConsumptionTab
         surgery={surgery}
         itemConsumptions={this.state.itemConsumptions}
         consumptionSearchTerm={this.state.consumptionSearchTerm}
         consumptionSearchResults={this.state.consumptionSearchResults}
         isSearchingConsumption={this.state.isSearchingConsumption}
         selectedConsumptionItems={this.state.selectedConsumptionItems}
         selectedLocation={this.state.selectedLocation}
         availableLocations={this.state.availableLocations}
         isLoadingLocations={this.state.isLoadingLocations}
         showConsumptionDialog={this.state.showConsumptionDialog}
         consumptionForm={this.state.consumptionForm}
         consumptionHeadOptions={this.state.consumptionHeadOptions}
         isCreatingConsumption={this.state.isCreatingConsumption}
         onLocationChange={this.handleLocationChange}
         onSearchTermChange={this.handleSearchTermChange}
         onSearchStockItems={this.searchStockItems}
         onLoadLocations={this.loadLocations}
         onClearSearchResults={this.clearSearchResults}
         onAddItemToConsumption={this.addItemToConsumption}
         onRemoveItemFromConsumption={this.removeItemFromConsumption}
         onOpenConsumptionDialog={this.openConsumptionDialog}
         onCloseConsumptionDialog={this.closeConsumptionDialog}
         onUpdateConsumptionForm={this.updateConsumptionForm}
         onCreateConsumption={this.createConsumption}
         canEdit={!this.isDischargedStatus() && !this.isCancelledStatus()}
        />
       }
      />

      {/* Tab 7: Billing */}
      <Tab
       id="billing"
       title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
         <span
          style={{
           fontWeight: 700,
           fontSize: "18px",
           lineHeight: 1,
           color: "var(--text-primary)",
          }}
         >
          ₹
         </span>
         <span>Billing</span>
        </div>
       }
       panel={
        <BillingTab
         ref={this.billingTabRef}
         surgery={surgery}
         surgeries={this.state.sxBooking.surgeries}
         itemConsumptions={this.state.itemConsumptions}
         onAnesthesiaChange={this.handleAnesthesiaChangeFromBilling}
         onPaymentsUpdate={this.handlePaymentsUpdate}
         onStatusChange={this.handleStatusChangeFromBilling}
         onSurgeryDataChange={this.handleBillingDataPatchFromTab}
         onBalanceCheck={this.ensureUnbilledIfBalanceNonZero}
         onRefreshPayorPanel={this.refreshPayorPanelFromServer}
         onServerStateChanged={this.reloadForServerChange}
         canEdit={!this.getBillingLockReason()}
         lockReason={this.getBillingLockReason()}
        />
       }
      />

      {/* Tab 8: Audit */}
      <Tab
       id="audit"
       title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
         <Icon icon={IconNames.HISTORY} />
         <span>Audit</span>
        </div>
       }
       panel={
        <AuditTab
         ref={this.auditTabRef}
         refreshTick={this.state.auditRefreshTick}
        />
       }
      />
     </Tabs>
    </Card>

    {/* Add Surgery Dialog */}
    <Dialog
     isOpen={this.state.showAddSurgeryDialog}
     onClose={this.closeAddSurgeryDialog}
     title="Add Surgery"
     className={this.getThemeClass()}
    >
     <div
      className={Classes.DIALOG_BODY}
      style={{
       backgroundColor: "var(--bg-primary)",
       color: "var(--text-primary)",
      }}
     >
      <FormGroup label="Surgery" labelFor="add-surgery-select">
       <Suggest
        items={this.filterAndSortSurgeryTypes(
         this.state.surgerySearchQuery,
         this.getFilteredPrintedNames()
        )}
        selectedItem={(() => {
         const filteredSurgeries = this.getFilteredPrintedNames();
         return (
          filteredSurgeries.find(
           (s) => s._id === this.state.selectedPrintedNameId
          ) || null
         );
        })()}
        inputValueRenderer={(item) => (item ? item.printedName : "")}
        itemPredicate={(query, item) => {
         // Items are already filtered and sorted by filterAndSortSurgeryTypes
         // Just do basic check here
         if (!query) return true;
         const searchTerm = query.toLowerCase().trim();
         const surgeryName = (item.printedName || "").toLowerCase();
         return surgeryName.includes(searchTerm);
        }}
        itemRenderer={(item, { handleClick, modifiers, query }) => {
         if (!modifiers.matchesPredicate) return null;

         return (
          <MenuItem
           key={item._id}
           active={modifiers.active}
           disabled={modifiers.disabled}
           onClick={handleClick}
           text={
            <div
             style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
             }}
            >
             <span>{this.highlightSearchText(item.printedName, query)}</span>
             {item.payor && (
              <Tag minimal small intent="primary" style={{ marginLeft: "8px" }}>
               {item.payor}
              </Tag>
             )}
            </div>
           }
           style={{ minWidth: "400px" }}
          />
         );
        }}
        onItemSelect={(selectedSurgery) => {
         this.setState({ selectedPrintedNameId: selectedSurgery._id });
        }}
        onQueryChange={(query) => {
         // Update search query for better UX
         this.setState({ surgerySearchQuery: query });
        }}
        noResults={<MenuItem disabled text="No surgeries found" />}
        popoverProps={{ minimal: true }}
        inputProps={{
         large: true,
         className: "v3-select",
         style: {
          backgroundColor: "var(--bg-surface)",
          color: "var(--text-primary)",
          width: "100%",
         },
         placeholder: this.state.isLoadingPrintedNames
          ? "Loading..."
          : "Search surgeries...",
         disabled: this.state.isLoadingPrintedNames,
        }}
       />
      </FormGroup>
      {this.state.sxBooking?.payorPanel && (
       <Tag minimal intent="primary" style={{ marginTop: "8px" }}>
        Filtered by payor: {this.state.sxBooking.payorPanel}
       </Tag>
      )}
     </div>
     <div
      className={Classes.DIALOG_FOOTER}
      style={{
       backgroundColor: "var(--bg-surface)",
       borderTop: "1px solid var(--border-color)",
      }}
     >
      <div className={Classes.DIALOG_FOOTER_ACTIONS}>
       <Button onClick={this.closeAddSurgeryDialog}>Cancel</Button>
       <Button
        intent="primary"
        onClick={this.confirmAddSurgery}
        disabled={!this.state.selectedPrintedNameId}
       >
        Add
       </Button>
      </div>
     </div>
    </Dialog>

    {/* Cancel Surgery Dialog */}
    <Dialog
     isOpen={this.state.showCancelDialog}
     onClose={this.closeCancelDialog}
     title="Cancel Surgery"
     className={this.getThemeClass()}
    >
     <div
      className={Classes.DIALOG_BODY}
      style={{
       backgroundColor: "var(--bg-surface)",
       color: "var(--text-primary)",
       paddingTop: 12,
      }}
     >
      <FormGroup
       label="Cancellation Reason"
       labelFor="cancel-reason-textarea"
       style={{ marginBottom: 0 }}
      >
       <TextArea
        id="cancel-reason-textarea"
        fill
        growVertically
        large
        rows={4}
        maxLength={500}
        autoFocus
        placeholder="Enter cancellation reason"
        value={this.state.cancelReason}
        onChange={this.handleCancelReasonChange}
        style={{ resize: "vertical" }}
       />
      </FormGroup>
     </div>
     <div
      className={Classes.DIALOG_FOOTER}
      style={{
       backgroundColor: "var(--bg-surface)",
       borderTop: "1px solid var(--border-color)",
      }}
     >
      <div className={Classes.DIALOG_FOOTER_ACTIONS} style={{ gap: 8 }}>
       <Button
        onClick={this.closeCancelDialog}
        disabled={this.state.isSubmittingCancel}
       >
        Close
       </Button>
       <Button
        intent="danger"
        onClick={this.submitCancellation}
        loading={this.state.isSubmittingCancel}
        disabled={!this.state.cancelReason.trim()}
       >
        Confirm Cancel
       </Button>
      </div>
     </div>
    </Dialog>

    {/* Mark Discharged Confirmation Dialog */}
    <Dialog
     isOpen={this.state.showMarkDischargedDialog}
     onClose={this.closeMarkDischargedDialog}
     title="Mark Discharged"
     className={this.getThemeClass()}
    >
     <div
      className={Classes.DIALOG_BODY}
      style={{
       backgroundColor: "var(--bg-surface)",
       color: "var(--text-primary)",
       paddingTop: 12,
      }}
     >
      {this.state.isCheckingDischarge ? (
       <Spinner size={24} />
      ) : this.state.dischargeBlockers.length > 0 ? (
       <div style={{ lineHeight: 1.5 }}>
        <div style={{ color: "var(--intent-danger, #db3737)", fontWeight: 600, marginBottom: 8 }}>
         This patient cannot be discharged yet:
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, color: "var(--text-primary)" }}>
         {this.state.dischargeBlockers.map((b) => (
          <li key={b}>{b}</li>
         ))}
        </ul>
       </div>
      ) : (
       <div style={{ color: "var(--text-secondary)", lineHeight: 1.5 }}>
        {this.state.dischargeWarnings.length > 0 && (
         <div style={{ marginBottom: 12 }}>
          <div style={{ color: "var(--intent-warning, #d9822b)", fontWeight: 600, marginBottom: 8 }}>
           Warning
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, color: "var(--text-primary)" }}>
           {this.state.dischargeWarnings.map((w) => (
            <li key={w}>{w}</li>
           ))}
          </ul>
         </div>
        )}
        This action will mark the surgery as <strong>Discharged</strong>. Are you
        sure you want to proceed?
        <br />
        <br />
        Certain actions cannot be performed for discharged surgeries.
       </div>
      )}
     </div>
     <div
      className={Classes.DIALOG_FOOTER}
      style={{
       backgroundColor: "var(--bg-surface)",
       borderTop: "1px solid var(--border-color)",
      }}
     >
      <div className={Classes.DIALOG_FOOTER_ACTIONS} style={{ gap: 8 }}>
       <Button
        onClick={this.closeMarkDischargedDialog}
        disabled={
         this.state.isSubmittingMarkDischarged || this.state.isDischarging
        }
       >
        Close
       </Button>
       <Button
        icon="confirm"
        onClick={this.confirmMarkDischarged}
        loading={
         this.state.isSubmittingMarkDischarged || this.state.isDischarging
        }
        disabled={
         this.state.isSubmittingMarkDischarged ||
         this.state.isDischarging ||
         this.state.isCheckingDischarge ||
         this.state.dischargeBlockers.length > 0
        }
        intent={
         this.state.dischargeWarnings.length > 0 &&
         this.state.dischargeBlockers.length === 0
          ? "warning"
          : "primary"
        }
       >
        Confirm Mark Discharged
       </Button>
      </div>
     </div>
    </Dialog>
   </div>
  );
 }
}

export default inject("authStore")(observer(withRouter(SurgeryBookingDetails)));
